import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
	AlertTriangle,
	Award,
	Briefcase,
	Calendar,
	CheckCircle2,
	ClipboardList,
	ChevronDown,
	ChevronUp,
	CircleX,
	Clock3,
	CreditCard,
	Eye,
	EyeOff,
	Filter,
	FileUser,
	Grid3X3,
	KeyRound,
	Languages,
	Mail,
	MapPin,
	MessageCircle,
	Pencil,
	Phone,
	Play,
	Plus,
	Save,
	Star,
	Table2,
	Trash2,
	User,
	X,
} from "lucide-react";
import { useChangeUserPassword } from "../../hooks/control";
import { requestApi } from "../../core/api/api";
import { logout } from "../../core/auth/auth";
import { useAuthSessionContext } from "../routing/auth-session-store";
import { BANKS_OPTIONS } from "../../core/constants/banks";
import { getImageUri } from "../../core/utils/imageLoad";
import type { DayAvailability, Period } from "../../core/types/provider-registration";
import {
	createDefaultAvailability,
	time12ToMinutes,
} from "../../core/utils/provider-registration";
import type {
	FaqCategory,
	FaqRow,
	PaymentMethodRow,
	PaymentMethodSavePayload,
	PaymentMethodType,
	ProfessionalPersonalInfo,
	ProviderAddressRow,
	ServiceRow,
	OrderRow,
	ProfessionalStats,
	ObtainedBadge,
} from "../../core/types/tsx/components/Profesional/ProfesionalDetails.types";
import { AvailabilitySwitch } from "../AvailabilitySwitch";
import { AvailabilityScheduleEditor } from "../common/AvailabilityScheduleEditor";
import { AddressCardsBoard } from "../common/AddressCardsBoard";
import { BadgeIcon } from "../common/BadgeIcon";
import FiltersPedidos from "../FiltersPedidos";
import { searchAdministrativeNominatim } from "../user-details/nominatim";
import type { AdministrativeSuggestion } from "../user-details/nominatim";
import { AlertModal, type AlertVariant } from "../modals/AlertModal";
import { ConfirmModal, type ConfirmVariant } from "../modals/ConfirmModal";
import type { FiltersState } from "../../core/types/tsx/components/FiltersPedidos.types";
import {
	composePhoneWithCountryCode,
	getPhoneCountryCodeOptions,
	getPhoneValidationError,
	splitPhoneInputValue,
	splitPhoneWithCountryCode,
} from "../../core/utils/phone";

/* ─── Tab definition ─── */
type TabKey = "solicitudes" | "pedidos" | "servicios" | "faq" | "cuenta" | "estadisticas";

type PedidosQuickFilter = "all" | "completado" | "en-proceso" | "por-cotizar";

type AccountSectionKey = "datos" | "direcciones" | "pagos" | "seguridad";

const MAX_FAQS_PER_CATEGORY = 4;

const TABS: { key: TabKey; label: string }[] = [
	{ key: "solicitudes", label: "Mis Solicitudes" },
	{ key: "pedidos", label: "Mis Pedidos" },
	{ key: "servicios", label: "Mis Servicios" },
	{ key: "faq", label: "Preguntas Frecuentes" },
	{ key: "cuenta", label: "Mi Cuenta" },
	{ key: "estadisticas", label: "Estadísticas" },
];

const EMPTY_PEDIDOS_FILTERS: FiltersState = {
	term: "",
	status: "",
	date: "",
	ubicacion: "",
};

type PaymentMethodForm = {
	methodId: number | null;
	methodType: PaymentMethodType | "";
	bankName: string;
	accountNumber: string;
	phoneNumber: string;
	identification: string;
	holderName: string;
	isDefault: boolean;
};

type PaymentMethodOption = {
	id: number;
	value: PaymentMethodType;
	label: string;
	description: string;
};

const PAYMENT_METHOD_OPTIONS: PaymentMethodOption[] = [
	{
		id: 1,
		value: "transferencia_bancaria",
		label: "Transferencia bancaria",
		description: "Cuenta bancaria nacional",
	},
	{
		id: 2,
		value: "pago_movil",
		label: "Pago móvil",
		description: "Teléfono o cédula",
	},
];

const PAYMENT_FORM_INITIAL: PaymentMethodForm = {
	methodId: null,
	methodType: "",
	bankName: "",
	accountNumber: "",
	phoneNumber: "",
	identification: "",
	holderName: "",
	isDefault: false,
};

const maskAccountNumber = (value: string) => {
	const digits = value.replace(/\D/g, "");
	if (!digits) return "****";
	return `****${digits.slice(-4)}`;
};

const toPaymentMethodLabel = (value: PaymentMethodType) =>
	PAYMENT_METHOD_OPTIONS.find((option) => option.value === value)?.label ?? "Método";

/* ─── Fallbacks & constants ─── */
const personalInfoFallback: ProfessionalPersonalInfo = {
	nombre: "-",
	oficio: "Proveedor",
	companyName: "Proveedor",
	experiencia: "-",
	idiomas: "-",
	ubicacion: "Sin ubicación",
	desde: "-",
	verificado: false,
	correo: "-",
	telefono: "-",
	biografia: "Sin biografía profesional",
	categoryIds: [],
};

const DAYS_ORDER: DayAvailability["day"][] = [
	"Lunes",
	"Martes",
	"Miércoles",
	"Jueves",
	"Viernes",
	"Sábado",
	"Domingo",
];

/* ─── Tiny shared UI helpers ─── */
const FieldLabel = ({ icon, label }: { icon: ReactNode; label: string }) => (
	<label className='mb-2 flex items-center gap-2 text-sm font-medium text-gray-600'>
		{icon}
		{label}
	</label>
);

const ScheduleRow = ({ day, hours }: { day: string; hours: string }) => (
	<div className='flex items-center justify-between rounded-xl border border-border px-4 py-3'>
		<span className='text-sm font-medium text-gray-700'>{day}</span>
		<span className='text-sm font-semibold text-text-placeholder'>{hours}</span>
	</div>
);

type PasswordFieldProps = {
	label: string;
	placeholder: string;
	show: boolean;
	onToggle: () => void;
	value: string;
	onChange: (value: string) => void;
};

const PasswordField = ({
	label,
	placeholder,
	show,
	onToggle,
	value,
	onChange,
}: PasswordFieldProps) => (
	<div>
		<label className='mb-2 block text-sm font-medium text-gray-700'>{label}</label>
		<div className='relative'>
			<input
				type={show ? "text" : "password"}
				placeholder={placeholder}
				value={value}
				onChange={(e) => onChange(e.target.value)}
				className='h-12 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 pr-12 text-sm text-gray-700 outline-none transition focus:border-primary'
			/>
			<button
				type='button'
				onClick={onToggle}
				className='cursor-pointer absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 transition hover:text-gray-600'
				aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
			>
				{show ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
			</button>
		</div>
	</div>
);

const formatScheduleToRange = (slot: DayAvailability) => {
	if (!slot.enabled) return "No Laboral";
	return `${slot.startTime} ${slot.startPeriod} - ${slot.endTime} ${slot.endPeriod}`;
};

/* ─── Badge for order status ─── */
const statusStyles: Record<string, string> = {
	Completado: "bg-green-50 text-green-700 border-green-200",
	"En Proceso": "bg-blue-50 text-blue-700 border-blue-200",
	Dialogando: "bg-[#ECD4FB] text-[#8313C8] border-[#8313C8]",
	Cancelado: "bg-red-50 text-red-700 border-red-200",
	"En Espera": "bg-[#DBEAFE] text-[#1447E6] border-[#1447E6]",
	Soporte: "bg-violet-50 text-violet-700 border-violet-200",
	Garantía: "bg-indigo-50 text-indigo-700 border-indigo-200",
	Pagado: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

type StructuredRequestDescription = {
	format?: string;
	schedule?: { date?: string; time?: string };
	items?: Array<{
		name?: string;
		quantity?: number;
		unitPrice?: number;
		lineTotal?: number;
	}>;
};

const parseStructuredRequestDescription = (
	raw?: string,
): StructuredRequestDescription | null => {
	if (!raw) return null;
	const marker = "SERVIX_REQUEST_V1::";
	if (!raw.startsWith(marker)) return null;
	const encoded = raw.slice(marker.length).trim();
	if (!encoded) return null;
	try {
		const parsed = JSON.parse(encoded) as StructuredRequestDescription;
		return parsed?.format === "SERVIX_REQUEST_V1" ? parsed : null;
	} catch {
		return null;
	}
};

type AlertConfig = {
	title: string;
	message: string;
	variant?: AlertVariant;
	confirmText?: string;
};

type ConfirmConfig = {
	title: string;
	message: string;
	variant?: ConfirmVariant;
	confirmLabel?: string;
	cancelLabel?: string;
};

type ProfessionalPersonalSavePayload = ProfessionalPersonalInfo & {
	firstName: string;
	lastName: string;
};

const splitFullName = (fullName: string): { firstName: string; lastName: string } => {
	const parts = fullName.trim().split(/\s+/).filter(Boolean);

	if (parts.length === 0) return { firstName: "", lastName: "" };
	if (parts.length === 1) return { firstName: parts[0], lastName: "" };
	if (parts.length === 2) return { firstName: parts[0], lastName: parts[1] };

	return {
		firstName: parts.slice(0, -2).join(" "),
		lastName: parts.slice(-2).join(" "),
	};
};

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
export const ProfesionalDetails = ({
	initialTab = "solicitudes",
	profile,
	usdToBsRate = 36,
	schedule,
	requests = [],
	orders = [],
	services = [],
	faqs = [],
	faqCategories = [],
	personalCategoryOptions = [],
	selectedPersonalCategoryIds = [],
	providerAddresses = [],
	paymentMethods = [],
	paymentMethodOptions = PAYMENT_METHOD_OPTIONS,
	stats,
	badges = [],
	onAddService,
	disableAddService,
	onViewService,
	onEditService,
	onToggleServiceStatus,
	togglingServiceId = null,
	onCreateFaq,
	onUpdateFaq,
	onDeleteFaq,
	onSavePersonalInfo,
	onSaveSchedule,
	onCreateProviderAddress,
	onUpdateProviderAddress,
	onToggleProviderAddress,
	onCreatePaymentMethod,
	onUpdatePaymentMethod,
	onDeletePaymentMethod,
	isSavingFaq = false,
	isSavingPersonalInfo = false,
	isSavingSchedule = false,
	isSavingAddress = false,
	isSavingPaymentMethod = false,
	onCotizar,
	onStartService,
	onCompleteOrder,
	onReportOrder,
	onAcceptSolicitud,
	onRejectSolicitud,
}: {
	initialTab?: "solicitudes" | "pedidos" | "estadisticas" | "cuenta";
	profile?: ProfessionalPersonalInfo;
	usdToBsRate?: number;
	schedule?: DayAvailability[];
	requests?: OrderRow[];
	orders?: OrderRow[];
	services?: ServiceRow[];
	faqs?: FaqRow[];
	faqCategories?: FaqCategory[];
	personalCategoryOptions?: FaqCategory[];
	selectedPersonalCategoryIds?: number[];
	providerAddresses?: ProviderAddressRow[];
	paymentMethods?: PaymentMethodRow[];
	paymentMethodOptions?: PaymentMethodOption[];
	stats?: ProfessionalStats;
	badges?: ObtainedBadge[];
	onAddService?: () => void;
	disableAddService?: boolean;
	onViewService?: (service: ServiceRow) => void;
	onEditService?: (service: ServiceRow) => void;
	onToggleServiceStatus?: (service: ServiceRow) => void;
	togglingServiceId?: number | null;
	onCreateFaq?: (payload: {
		categoryId: number;
		question: string;
		answer: string;
	}) => Promise<void>;
	onUpdateFaq?: (
		faqId: number,
		payload: {
			categoryId: number;
			question: string;
			answer: string;
		},
	) => Promise<void>;
	onDeleteFaq?: (faqId: number) => Promise<void>;
	onSavePersonalInfo?: (
		payload: ProfessionalPersonalSavePayload,
	) => Promise<void | boolean>;
	onSaveSchedule?: (payload: DayAvailability[]) => Promise<void>;
	onCreateProviderAddress?: (payload: { state: string; city: string }) => Promise<void>;
	onUpdateProviderAddress?: (
		addressId: number,
		payload: { state: string; city: string },
	) => Promise<void>;
	onToggleProviderAddress?: (addressId: number, nextActive: boolean) => Promise<void>;
	onCreatePaymentMethod?: (payload: PaymentMethodSavePayload) => Promise<void>;
	onUpdatePaymentMethod?: (
		paymentMethodId: number,
		payload: PaymentMethodSavePayload,
	) => Promise<void>;
	onDeletePaymentMethod?: (paymentMethodId: number) => Promise<void>;
	isSavingFaq?: boolean;
	isSavingPersonalInfo?: boolean;
	isSavingSchedule?: boolean;
	isSavingAddress?: boolean;
	isSavingPaymentMethod?: boolean;
	onCotizar?: (order: OrderRow) => void;
	onStartService?: (order: OrderRow) => Promise<void> | void;
	onCompleteOrder?: (order: OrderRow) => Promise<void> | void;
	onReportOrder?: (order: OrderRow) => Promise<void> | void;
	onAcceptSolicitud?: (request: OrderRow) => Promise<void>;
	onRejectSolicitud?: (request: OrderRow) => Promise<void>;
}) => {
	const resolvedPaymentMethodOptions =
		paymentMethodOptions.length > 0 ? paymentMethodOptions : PAYMENT_METHOD_OPTIONS;

	const navigate = useNavigate();
	const [alertState, setAlertState] = useState<AlertConfig & { open: boolean }>({
		open: false,
		title: "",
		message: "",
		variant: "info",
		confirmText: "Entendido",
	});
	const [confirmState, setConfirmState] = useState<ConfirmConfig & { open: boolean }>({
		open: false,
		title: "",
		message: "",
		variant: "primary",
		confirmLabel: "Confirmar",
		cancelLabel: "Cancelar",
	});
	const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);

	const showAlert = (config: AlertConfig) => {
		setAlertState({
			open: true,
			title: config.title,
			message: config.message,
			variant: config.variant ?? "info",
			confirmText: config.confirmText ?? "Entendido",
		});
	};

	const handleCloseAlert = () => {
		setAlertState((prev) => ({ ...prev, open: false }));
	};

	const requestConfirmation = (config: ConfirmConfig) =>
		new Promise<boolean>((resolve) => {
			confirmResolverRef.current = resolve;
			setConfirmState({
				open: true,
				title: config.title,
				message: config.message,
				variant: config.variant ?? "primary",
				confirmLabel: config.confirmLabel ?? "Confirmar",
				cancelLabel: config.cancelLabel ?? "Cancelar",
			});
		});

	const resolveConfirm = (value: boolean) => {
		confirmResolverRef.current?.(value);
		confirmResolverRef.current = null;
		setConfirmState((prev) => ({ ...prev, open: false }));
	};

	const handleConfirmAccept = () => {
		resolveConfirm(true);
	};

	const handleConfirmCancel = () => {
		resolveConfirm(false);
	};

	/* ── active tab ── */
	const resolveInitialTab = (
		tab: "solicitudes" | "pedidos" | "estadisticas" | "cuenta" | undefined,
	): TabKey => {
		if (tab === "pedidos") return "pedidos";
		if (tab === "estadisticas") return "estadisticas";
		if (tab === "cuenta") return "cuenta";
		return "solicitudes";
	};

	const [activeTab, setActiveTab] = useState<TabKey>(() =>
		resolveInitialTab(initialTab),
	);
	const [activeAccountSection, setActiveAccountSection] =
		useState<AccountSectionKey>("datos");

	useEffect(() => {
		setActiveTab(resolveInitialTab(initialTab));
	}, [initialTab]);

	/* ── pedidos view toggle ── */
	const [pedidosView, setPedidosView] = useState<"table" | "cards">("table");
	const [showPedidosFilters, setShowPedidosFilters] = useState(false);
	const [pedidosFilters, setPedidosFilters] =
		useState<FiltersState>(EMPTY_PEDIDOS_FILTERS);
	const [pedidosQuickFilter, setPedidosQuickFilter] =
		useState<PedidosQuickFilter>("all");
	const [totalsCurrency, setTotalsCurrency] = useState<"USD" | "Bs">("USD");
	const [faqCategoryFilter, setFaqCategoryFilter] = useState<number | "all">("all");
	const [expandedFaqId, setExpandedFaqId] = useState<number | null>(null);
	const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
	const [editingFaq, setEditingFaq] = useState<FaqRow | null>(null);
	const [faqForm, setFaqForm] = useState<{
		categoryId: string;
		question: string;
		answer: string;
	}>({
		categoryId: "",
		question: "",
		answer: "",
	});
	const [faqFormErrors, setFaqFormErrors] = useState<{
		categoryId?: string;
		question?: string;
		answer?: string;
	}>({});
	const [expandedSolicitudIds, setExpandedSolicitudIds] = useState<string[]>([]);
	const [solicitudFilter, setSolicitudFilter] = useState<
		"pendiente" | "aprobado" | "cancelado"
	>("pendiente");

	/* ── resolved profile ── */
	const resolvedProfile = profile ?? personalInfoFallback;

	const resolvedSchedule = useMemo(() => {
		const fallback = createDefaultAvailability();
		if (!schedule || schedule.length === 0) return fallback;
		const byDay = new Map(schedule.map((item) => [item.day, item]));
		return DAYS_ORDER.map(
			(day) => byDay.get(day) ?? fallback.find((slot) => slot.day === day)!,
		).filter(Boolean);
	}, [schedule]);

	const [isEditingPersonal, setIsEditingPersonal] = useState(false);
	const [isEditingSchedule, setIsEditingSchedule] = useState(false);

	const [personalForm, setPersonalForm] =
		useState<ProfessionalPersonalInfo>(resolvedProfile);
	const [personalFormErrors, setPersonalFormErrors] = useState<{
		nombre?: string;
		apellido?: string;
		correo?: string;
		telefono?: string;
		experiencia?: string;
	}>({});
	const [personalCategoryIds, setPersonalCategoryIds] = useState<number[]>(
		selectedPersonalCategoryIds,
	);
	const [isPaymentMethodModalOpen, setIsPaymentMethodModalOpen] = useState(false);
	const [editingPaymentMethodId, setEditingPaymentMethodId] = useState<number | null>(
		null,
	);
	const [paymentMethodForm, setPaymentMethodForm] =
		useState<PaymentMethodForm>(PAYMENT_FORM_INITIAL);
	const [paymentMethodErrors, setPaymentMethodErrors] = useState<{
		methodId?: string;
		methodType?: string;
		bankName?: string;
		accountNumber?: string;
		phoneNumber?: string;
		identification?: string;
		holderName?: string;
	}>({});
	const [isProviderAddressModalOpen, setIsProviderAddressModalOpen] = useState(false);
	const [editingProviderAddress, setEditingProviderAddress] =
		useState<ProviderAddressRow | null>(null);
	const [providerAddressForm, setProviderAddressForm] = useState<{
		state: string;
		city: string;
	}>({
		state: "",
		city: "",
	});
	const [providerAddressErrors, setProviderAddressErrors] = useState<{
		state?: string;
		city?: string;
	}>({});
	const [stateSuggestions, setStateSuggestions] = useState<AdministrativeSuggestion[]>(
		[],
	);
	const [citySuggestions, setCitySuggestions] = useState<AdministrativeSuggestion[]>(
		[],
	);
	const [stateSuggestStatus, setStateSuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const [citySuggestStatus, setCitySuggestStatus] = useState<
		"idle" | "loading" | "error"
	>("idle");
	const stateSuggestAbortRef = useRef<AbortController | null>(null);
	const citySuggestAbortRef = useRef<AbortController | null>(null);
	const stateSuggestDebounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(
		null,
	);
	const citySuggestDebounceRef = useRef<ReturnType<typeof window.setTimeout> | null>(
		null,
	);
	const [scheduleForm, setScheduleForm] = useState<DayAvailability[]>(resolvedSchedule);
	const [scheduleValidationError, setScheduleValidationError] = useState<string | null>(
		null,
	);
	const [isPersonalCategoriesDropdownOpen, setIsPersonalCategoriesDropdownOpen] =
		useState(false);
	const personalCategoriesDropdownRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		return () => {
			stateSuggestAbortRef.current?.abort();
			citySuggestAbortRef.current?.abort();
			if (stateSuggestDebounceRef.current) {
				window.clearTimeout(stateSuggestDebounceRef.current);
			}
			if (citySuggestDebounceRef.current) {
				window.clearTimeout(citySuggestDebounceRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!isFaqModalOpen && !isProviderAddressModalOpen && !isPaymentMethodModalOpen)
			return;

		const previousBodyOverflow = document.body.style.overflow;
		const previousHtmlOverflow = document.documentElement.style.overflow;
		document.body.style.overflow = "hidden";
		document.documentElement.style.overflow = "hidden";

		return () => {
			document.body.style.overflow = previousBodyOverflow;
			document.documentElement.style.overflow = previousHtmlOverflow;
		};
	}, [isFaqModalOpen, isProviderAddressModalOpen, isPaymentMethodModalOpen]);

	useEffect(() => {
		if (!isPersonalCategoriesDropdownOpen) return;

		const handleOutsideClick = (event: MouseEvent) => {
			const target = event.target as Node | null;
			if (!target) return;
			if (personalCategoriesDropdownRef.current?.contains(target)) return;
			setIsPersonalCategoriesDropdownOpen(false);
		};

		document.addEventListener("mousedown", handleOutsideClick);
		return () => document.removeEventListener("mousedown", handleOutsideClick);
	}, [isPersonalCategoriesDropdownOpen]);

	useEffect(() => {
		if (!isEditingPersonal) {
			setIsPersonalCategoriesDropdownOpen(false);
		}
	}, [isEditingPersonal]);

	const refreshPersonalForm = () => {
		setPersonalForm(resolvedProfile);
		setPersonalCategoryIds(selectedPersonalCategoryIds);
	};
	const refreshScheduleForm = () => {
		setScheduleForm(resolvedSchedule);
		setScheduleValidationError(null);
	};

	const sortedSchedule = useMemo(() => {
		const indexByDay = new Map(DAYS_ORDER.map((day, index) => [day, index]));
		return [...(isEditingSchedule ? scheduleForm : resolvedSchedule)].sort(
			(a, b) => (indexByDay.get(a.day) ?? 99) - (indexByDay.get(b.day) ?? 99),
		);
	}, [isEditingSchedule, scheduleForm, resolvedSchedule]);

	const sortedPersonalCategoryOptions = useMemo(
		() =>
			[...personalCategoryOptions].sort((a, b) =>
				a.label.localeCompare(b.label, "es", { sensitivity: "base" }),
			),
		[personalCategoryOptions],
	);

	const availablePersonalCategoryOptions = useMemo(
		() =>
			sortedPersonalCategoryOptions.filter(
				(option) => !selectedPersonalCategoryIds.includes(option.id),
			),
		[sortedPersonalCategoryOptions, selectedPersonalCategoryIds],
	);

	const personalCategoryLabelById = useMemo(
		() =>
			new Map(
				sortedPersonalCategoryOptions.map((category) => [
					category.id,
					category.label,
				]),
			),
		[sortedPersonalCategoryOptions],
	);

	const orderedPersonalCategoryIds = useMemo(() => {
		const selectedIds = new Set(
			personalCategoryIds.filter((categoryId) =>
				sortedPersonalCategoryOptions.some((option) => option.id === categoryId),
			),
		);

		return sortedPersonalCategoryOptions
			.map((category) => category.id)
			.filter((categoryId) => selectedIds.has(categoryId));
	}, [personalCategoryIds, sortedPersonalCategoryOptions]);

	const selectedPersonalCategoryIdSet = useMemo(
		() => new Set(orderedPersonalCategoryIds),
		[orderedPersonalCategoryIds],
	);

	const normalizedInitialPersonalCategoryIds = useMemo(() => {
		const selectedIds = new Set(
			selectedPersonalCategoryIds.filter((categoryId) =>
				sortedPersonalCategoryOptions.some((option) => option.id === categoryId),
			),
		);

		return sortedPersonalCategoryOptions
			.map((category) => category.id)
			.filter((categoryId) => selectedIds.has(categoryId));
	}, [selectedPersonalCategoryIds, sortedPersonalCategoryOptions]);

	const selectedPersonalCategoryLabels = useMemo(
		() =>
			orderedPersonalCategoryIds.map(
				(categoryId) =>
					personalCategoryLabelById.get(categoryId) ??
					`Categoría ${categoryId}`,
			),
		[orderedPersonalCategoryIds, personalCategoryLabelById],
	);

	const areAllPersonalCategoriesSelected = useMemo(
		() =>
			availablePersonalCategoryOptions.length > 0 &&
			availablePersonalCategoryOptions.every((opt) =>
				selectedPersonalCategoryIdSet.has(opt.id),
			),
		[availablePersonalCategoryOptions, selectedPersonalCategoryIdSet],
	);

	const hasUnsavedPersonalChanges = useMemo(() => {
		if (!isEditingPersonal) return false;

		const currentName = (personalForm.nombre ?? "").trim();
		const initialName = (resolvedProfile.nombre ?? "").trim();
		const currentCompanyName = (
			personalForm.companyName ??
			personalForm.oficio ??
			""
		).trim();
		const initialCompanyName = (
			resolvedProfile.companyName ??
			resolvedProfile.oficio ??
			""
		).trim();
		const currentExperience = String(personalForm.experiencia ?? "")
			.replace(/[^\d]/g, "")
			.trim();
		const initialExperience = String(resolvedProfile.experiencia ?? "")
			.replace(/[^\d]/g, "")
			.trim();
		const currentLanguages = (personalForm.idiomas ?? "").trim();
		const initialLanguages = (resolvedProfile.idiomas ?? "").trim();
		const currentEmail = (personalForm.correo ?? "").trim();
		const initialEmail = (resolvedProfile.correo ?? "").trim();
		const currentPhone = (personalForm.telefono ?? "").trim();
		const initialPhone = (resolvedProfile.telefono ?? "").trim();
		const currentBio = (personalForm.biografia ?? "").trim();
		const initialBio = (resolvedProfile.biografia ?? "").trim();

		const hasCategoryChanges =
			orderedPersonalCategoryIds.length !==
				normalizedInitialPersonalCategoryIds.length ||
			orderedPersonalCategoryIds.some(
				(categoryId, index) =>
					categoryId !== normalizedInitialPersonalCategoryIds[index],
			);

		return (
			currentName !== initialName ||
			currentCompanyName !== initialCompanyName ||
			currentExperience !== initialExperience ||
			currentLanguages !== initialLanguages ||
			currentEmail !== initialEmail ||
			currentPhone !== initialPhone ||
			currentBio !== initialBio ||
			hasCategoryChanges
		);
	}, [
		isEditingPersonal,
		personalForm.biografia,
		personalForm.companyName,
		personalForm.correo,
		personalForm.experiencia,
		personalForm.idiomas,
		personalForm.nombre,
		personalForm.oficio,
		personalForm.telefono,
		resolvedProfile.biografia,
		resolvedProfile.companyName,
		resolvedProfile.correo,
		resolvedProfile.experiencia,
		resolvedProfile.idiomas,
		resolvedProfile.nombre,
		resolvedProfile.oficio,
		resolvedProfile.telefono,
		normalizedInitialPersonalCategoryIds,
		orderedPersonalCategoryIds,
	]);

	const hasUnsavedScheduleChanges = useMemo(() => {
		if (!isEditingSchedule) return false;

		const currentByDay = new Map(scheduleForm.map((slot) => [slot.day, slot]));
		const initialByDay = new Map(resolvedSchedule.map((slot) => [slot.day, slot]));

		if (currentByDay.size !== initialByDay.size) return true;

		for (const [day, initialSlot] of initialByDay.entries()) {
			const currentSlot = currentByDay.get(day);
			if (!currentSlot) return true;

			if (
				currentSlot.enabled !== initialSlot.enabled ||
				currentSlot.startTime !== initialSlot.startTime ||
				currentSlot.endTime !== initialSlot.endTime ||
				currentSlot.startPeriod !== initialSlot.startPeriod ||
				currentSlot.endPeriod !== initialSlot.endPeriod
			) {
				return true;
			}
		}

		return false;
	}, [isEditingSchedule, scheduleForm, resolvedSchedule]);

	const hasUnsavedAccountChanges =
		hasUnsavedPersonalChanges || hasUnsavedScheduleChanges;

	useEffect(() => {
		if (!hasUnsavedAccountChanges) return;

		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [hasUnsavedAccountChanges]);

	const selectedFaqCategoryIds = useMemo(() => {
		const ids = new Set<number>();
		selectedPersonalCategoryIds.forEach((categoryId) => {
			if (typeof categoryId === "number" && categoryId > 0) {
				ids.add(categoryId);
			}
		});
		return ids;
	}, [selectedPersonalCategoryIds]);

	const faqCategoryIdsFromRows = useMemo(() => {
		const ids = new Set<number>();
		faqs.forEach((faq) => {
			if (typeof faq.categoryId === "number" && faq.categoryId > 0) {
				ids.add(faq.categoryId);
			}
		});
		return ids;
	}, [faqs]);

	const allowedFaqCategoryIds = useMemo(() => {
		const ids = new Set<number>();
		selectedFaqCategoryIds.forEach((categoryId) => {
			ids.add(categoryId);
		});
		faqCategoryIdsFromRows.forEach((categoryId) => {
			ids.add(categoryId);
		});
		return ids;
	}, [faqCategoryIdsFromRows, selectedFaqCategoryIds]);

	useEffect(() => {
		if (isEditingPersonal) return;
		setPersonalCategoryIds(selectedPersonalCategoryIds);
	}, [isEditingPersonal, selectedPersonalCategoryIds]);

	const getSolicitudStatus = (order: OrderRow) => {
		const rawStatus = String(order.estado ?? "").toLowerCase();
		if (rawStatus.includes("cancel") || rawStatus.includes("rechaz")) {
			return "cancelado" as const;
		}
		if (
			rawStatus.includes("aprob") ||
			rawStatus.includes("acept") ||
			rawStatus.includes("proceso") ||
			rawStatus.includes("complet") ||
			rawStatus.includes("pagad") ||
			rawStatus.includes("garant") ||
			rawStatus.includes("soporte")
		) {
			return "aprobado" as const;
		}
		return "pendiente" as const;
	};

	const isCompletedOrderForQuotation = (status?: string) => {
		const normalized = String(status ?? "").toLowerCase();
		return normalized.includes("complet") || normalized.includes("pagad");
	};

	const pedidosOrders = useMemo(() => orders, [orders]);

	const normalizeFilterText = useCallback(
		(value?: string | number | null) =>
			String(value ?? "")
				.toLowerCase()
				.normalize("NFD")
				.replace(/\p{Diacritic}/gu, "")
				.replace(/[-_]/g, " ")
				.replace(/\s+/g, " ")
				.trim(),
		[],
	);

	const extractOrderIsoDate = (order: OrderRow) => {
		const raw = order.orderDateRaw;
		if (typeof raw === "string" && raw.trim()) {
			const prefix = /^(\d{4}-\d{2}-\d{2})/.exec(raw.trim());
			if (prefix) return prefix[1];

			const parsed = new Date(raw);
			if (!Number.isNaN(parsed.getTime())) {
				const year = parsed.getFullYear();
				const month = String(parsed.getMonth() + 1).padStart(2, "0");
				const day = String(parsed.getDate()).padStart(2, "0");
				return `${year}-${month}-${day}`;
			}
		}

		const fromDisplay = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(
			String(order.fecha ?? "").trim(),
		);
		if (fromDisplay) {
			const [, day, month, year] = fromDisplay;
			return `${year}-${month}-${day}`;
		}

		return "";
	};

	const isConsolidatedOrder = useCallback(
		(status?: string) => {
			const normalized = normalizeFilterText(status);
			return normalized.includes("complet") || normalized.includes("pagad");
		},
		[normalizeFilterText],
	);

	const isInProgressOrderForQuickFilter = useCallback(
		(status?: string) => {
			const normalized = normalizeFilterText(status);
			return (
				normalized.includes("en proceso") || normalized.includes("en progreso")
			);
		},
		[normalizeFilterText],
	);

	const isQuotableOrderByQuickFilter = useCallback(
		(order: OrderRow) => {
			const normalized = normalizeFilterText(order.estado);
			const isDialogOrWaiting =
				normalized.includes("dialog") || normalized.includes("esper");
			return isDialogOrWaiting && !order.hasPendingQuotation;
		},
		[normalizeFilterText],
	);

	const filteredPedidosOrders = useMemo(() => {
		const term = normalizeFilterText(pedidosFilters.term);
		const status = normalizeFilterText(pedidosFilters.status);
		const date = String(pedidosFilters.date ?? "").trim();
		const ubicacion = normalizeFilterText(pedidosFilters.ubicacion);

		return pedidosOrders.filter((order) => {
			const searchableText = [
				order.numeroPedido,
				order.id,
				order.cliente,
				order.servicio,
				order.categoria,
				order.subcategoria,
				order.total,
				order.ubicacion,
			]
				.map((value) => normalizeFilterText(value))
				.join(" ");

			const matchesTerm = term ? searchableText.includes(term) : true;
			const normalizedOrderStatus = normalizeFilterText(order.estado);
			const matchesStatus = status
				? normalizedOrderStatus === status ||
					normalizedOrderStatus.includes(status)
				: true;
			const matchesDate = date ? extractOrderIsoDate(order) === date : true;
			const matchesUbicacion = ubicacion
				? normalizeFilterText(order.ubicacion).includes(ubicacion)
				: true;

			const matchesQuickFilter =
				pedidosQuickFilter === "all"
					? true
					: pedidosQuickFilter === "completado"
						? isConsolidatedOrder(order.estado)
						: pedidosQuickFilter === "en-proceso"
							? isInProgressOrderForQuickFilter(order.estado)
							: isQuotableOrderByQuickFilter(order);

			return (
				matchesTerm &&
				matchesStatus &&
				matchesDate &&
				matchesUbicacion &&
				matchesQuickFilter
			);
		});
	}, [
		pedidosFilters,
		pedidosOrders,
		pedidosQuickFilter,
		normalizeFilterText,
		isConsolidatedOrder,
		isInProgressOrderForQuickFilter,
		isQuotableOrderByQuickFilter,
	]);

	const pedidosStatusOptions = useMemo(
		() => [
			{ value: "Dialogando", label: "Dialogando" },
			{ value: "En Espera", label: "En Espera" },
			{ value: "En Proceso", label: "En Proceso" },
			{ value: "Completado", label: "Completado" },
			{ value: "Pagado", label: "Pagado" },
			{ value: "Soporte", label: "Soporte" },
			{ value: "Garantía", label: "Garantía" },
			{ value: "Cancelado", label: "Cancelado" },
		],
		[],
	);

	const clearPedidosFilters = () => {
		setPedidosFilters(EMPTY_PEDIDOS_FILTERS);
		setPedidosQuickFilter("all");
	};

	const parseAmountFromDisplay = (raw?: string | null) => {
		if (!raw || raw.trim() === "-") return null;
		const normalized = raw
			.replace(/[^\d,.-]/g, "")
			.replace(/\./g, "")
			.replace(",", ".");
		const parsed = Number(normalized);
		return Number.isFinite(parsed) ? parsed : null;
	};

	const formatOrderTotal = (order: OrderRow) => {
		const baseAmount =
			typeof order.totalAmount === "number" && Number.isFinite(order.totalAmount)
				? order.totalAmount
				: parseAmountFromDisplay(order.total);
		if (baseAmount === null) return "-";

		if (totalsCurrency === "Bs") {
			const effectiveRate =
				Number.isFinite(usdToBsRate) && usdToBsRate > 0 ? usdToBsRate : 36;
			const converted = baseAmount * effectiveRate;
			return new Intl.NumberFormat("es-VE", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(converted);
		}

		return new Intl.NumberFormat("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(baseAmount);
	};

	const formatServiceBasePrice = (service: ServiceRow) => {
		const baseAmount = parseAmountFromDisplay(service.precio);
		if (baseAmount === null) return "-";

		if (totalsCurrency === "Bs") {
			const effectiveRate =
				Number.isFinite(usdToBsRate) && usdToBsRate > 0 ? usdToBsRate : 36;
			const converted = baseAmount * effectiveRate;
			return new Intl.NumberFormat("es-VE", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2,
			}).format(converted);
		}

		return new Intl.NumberFormat("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(baseAmount);
	};

	const getOrderDetailsId = (order: OrderRow) => {
		const fromOrderId = Number(order.orderId ?? 0);
		if (Number.isFinite(fromOrderId) && fromOrderId > 0) {
			return fromOrderId;
		}

		const fromOrderCode = Number(order.numeroPedido);
		if (Number.isFinite(fromOrderCode) && fromOrderCode > 0) {
			return fromOrderCode;
		}
		const fromRowId = Number(String(order.id).replace(/\D/g, ""));
		if (Number.isFinite(fromRowId) && fromRowId > 0) {
			return fromRowId;
		}
		return null;
	};

	const handleViewOrderDetails = (order: OrderRow) => {
		const detailsId = getOrderDetailsId(order);
		if (!detailsId) return;
		navigate(`/info/pedido/${detailsId}`, {
			state: { orderId: detailsId },
		});
	};

	const handleOpenChat = (order: OrderRow) => {
		const detailsId = getOrderDetailsId(order);
		if (!detailsId) {
			navigate("/chat-expanded");
			return;
		}
		navigate("/chat-expanded", { state: { orderId: detailsId } });
	};

	const isOrderWaitingStatus = (status: string) => status === "En Espera";

	const isOrderInProgressStatus = (status: string) => {
		const normalized = normalizeFilterText(status);
		return (
			normalized.includes("en proceso") ||
			normalized.includes("en progreso") ||
			normalized.includes("proceso") ||
			normalized.includes("progreso")
		);
	};

	const resolvedFaqCategories = useMemo(() => {
		if (allowedFaqCategoryIds.size === 0) return [];

		const byId = new Map<number, string>();

		faqCategories.forEach((category) => {
			if (typeof category.id !== "number" || category.id <= 0) return;
			if (!allowedFaqCategoryIds.has(category.id)) return;
			const label = category.label?.trim();
			if (!label) return;
			byId.set(category.id, label);
		});

		allowedFaqCategoryIds.forEach((categoryId) => {
			if (byId.has(categoryId)) return;
			const fallbackLabel = personalCategoryLabelById.get(categoryId);
			byId.set(categoryId, fallbackLabel ?? `Categoría ${categoryId}`);
		});

		return Array.from(byId.entries())
			.map(([id, label]) => ({ id, label }))
			.sort((a, b) =>
				a.label.localeCompare(b.label, "es", { sensitivity: "base" }),
			);
	}, [allowedFaqCategoryIds, faqCategories, personalCategoryLabelById]);

	const visibleFaqs = useMemo(() => {
		if (selectedFaqCategoryIds.size === 0) return faqs;

		const hasOutOfSyncCategories = Array.from(faqCategoryIdsFromRows).some(
			(categoryId) => !selectedFaqCategoryIds.has(categoryId),
		);

		if (hasOutOfSyncCategories) {
			return faqs;
		}

		return faqs.filter((faq) => selectedFaqCategoryIds.has(faq.categoryId));
	}, [faqCategoryIdsFromRows, faqs, selectedFaqCategoryIds]);

	const faqLabelById = useMemo(
		() =>
			new Map(
				resolvedFaqCategories.map((category) => [category.id, category.label]),
			),
		[resolvedFaqCategories],
	);

	const filteredFaqs = useMemo(
		() =>
			faqCategoryFilter === "all"
				? visibleFaqs
				: visibleFaqs.filter((faq) => faq.categoryId === faqCategoryFilter),
		[faqCategoryFilter, visibleFaqs],
	);

	const faqCountByCategory = useMemo(() => {
		const counts = new Map<number, number>();
		visibleFaqs.forEach((faq) => {
			counts.set(faq.categoryId, (counts.get(faq.categoryId) ?? 0) + 1);
		});
		return counts;
	}, [visibleFaqs]);

	const groupedFaqs = useMemo(() => {
		const groups = new Map<number, { id: number; label: string; items: FaqRow[] }>();

		filteredFaqs.forEach((faq) => {
			const categoryId = faq.categoryId;
			const label = faqLabelById.get(categoryId) ?? `Categoría ${categoryId}`;
			if (!groups.has(categoryId)) {
				groups.set(categoryId, { id: categoryId, label, items: [] });
			}
			groups.get(categoryId)?.items.push(faq);
		});

		return Array.from(groups.values())
			.map((group) => ({
				...group,
				items: [...group.items].sort(
					(a, b) => (a.orderDisplay ?? 0) - (b.orderDisplay ?? 0),
				),
			}))
			.sort((a, b) =>
				a.label.localeCompare(b.label, "es", { sensitivity: "base" }),
			);
	}, [filteredFaqs, faqLabelById]);

	useEffect(() => {
		if (faqCategoryFilter === "all") return;
		const exists = resolvedFaqCategories.some(
			(category) => category.id === faqCategoryFilter,
		);
		if (!exists) setFaqCategoryFilter("all");
	}, [faqCategoryFilter, resolvedFaqCategories]);

	useEffect(() => {
		if (expandedFaqId === null) return;
		const visible = filteredFaqs.some((faq) => faq.id === expandedFaqId);
		if (!visible) setExpandedFaqId(null);
	}, [expandedFaqId, filteredFaqs]);

	const openCreateFaqModal = () => {
		const defaultCategoryId =
			faqCategoryFilter !== "all"
				? String(faqCategoryFilter)
				: resolvedFaqCategories[0]
					? String(resolvedFaqCategories[0].id)
					: "";

		setEditingFaq(null);
		setFaqForm({
			categoryId: defaultCategoryId,
			question: "",
			answer: "",
		});
		setFaqFormErrors({});
		setIsFaqModalOpen(true);
	};

	const openEditFaqModal = (faq: FaqRow) => {
		setEditingFaq(faq);
		setFaqForm({
			categoryId: String(faq.categoryId),
			question: faq.question,
			answer: faq.answer,
		});
		setFaqFormErrors({});
		setIsFaqModalOpen(true);
	};

	const closeFaqModal = () => {
		if (isSavingFaq) return;
		setIsFaqModalOpen(false);
		setEditingFaq(null);
		setFaqFormErrors({});
	};

	const handleFaqFieldChange = (
		field: "categoryId" | "question" | "answer",
		value: string,
	) => {
		setFaqForm((prev) => ({
			...prev,
			[field]: value,
		}));
		if (faqFormErrors[field]) {
			setFaqFormErrors((prev) => ({ ...prev, [field]: undefined }));
		}
	};

	const handleSubmitFaq = async () => {
		const categoryId = Number(faqForm.categoryId);
		const question = faqForm.question.trim();
		const answer = faqForm.answer.trim();

		const nextErrors: {
			categoryId?: string;
			question?: string;
			answer?: string;
		} = {};

		if (!Number.isFinite(categoryId) || categoryId <= 0) {
			nextErrors.categoryId = "Selecciona una categoría";
		} else if (!allowedFaqCategoryIds.has(categoryId)) {
			nextErrors.categoryId = "Selecciona una categoría válida";
		} else {
			const currentCategoryCount = faqCountByCategory.get(categoryId) ?? 0;
			const isChangingCategory =
				editingFaq !== null && editingFaq.categoryId !== categoryId;
			const reachesLimit =
				editingFaq === null
					? currentCategoryCount >= MAX_FAQS_PER_CATEGORY
					: isChangingCategory && currentCategoryCount >= MAX_FAQS_PER_CATEGORY;

			if (reachesLimit) {
				nextErrors.categoryId = `Máximo ${MAX_FAQS_PER_CATEGORY} preguntas por categoría`;
			}
		}
		if (!question) {
			nextErrors.question = "Escribe la pregunta";
		}
		if (!answer) {
			nextErrors.answer = "Escribe la respuesta";
		}

		if (Object.keys(nextErrors).length > 0) {
			setFaqFormErrors(nextErrors);
			return;
		}

		try {
			if (editingFaq && onUpdateFaq) {
				await onUpdateFaq(editingFaq.id, { categoryId, question, answer });
			} else if (onCreateFaq) {
				await onCreateFaq({ categoryId, question, answer });
			}

			setIsFaqModalOpen(false);
			setEditingFaq(null);
			setFaqFormErrors({});
		} catch (err) {
			showAlert({
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo guardar la pregunta frecuente",
				variant: "error",
			});
		}
	};

	const handleDeleteFaqItem = async (faq: FaqRow) => {
		if (!onDeleteFaq) return;

		const confirmed = await requestConfirmation({
			title: "¿Eliminar pregunta frecuente?",
			message: "Esta acción no se puede deshacer.",
			variant: "danger",
			confirmLabel: "Eliminar",
			cancelLabel: "Cancelar",
		});

		if (!confirmed) return;

		try {
			await onDeleteFaq(faq.id);
			if (expandedFaqId === faq.id) {
				setExpandedFaqId(null);
			}
		} catch (err) {
			showAlert({
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo eliminar la pregunta frecuente",
				variant: "error",
			});
		}
	};

	/* ── personal info handlers ── */
	const handleStartEditPersonal = () => {
		refreshPersonalForm();
		setPersonalFormErrors({});
		setIsPersonalCategoriesDropdownOpen(false);
		setIsEditingPersonal(true);
	};

	const handleCancelEditPersonal = () => {
		refreshPersonalForm();
		setPersonalFormErrors({});
		setIsPersonalCategoriesDropdownOpen(false);
		setIsEditingPersonal(false);
	};

	const showUnsavedChangesWarning = () => {
		showAlert({
			title: "Cambios sin guardar",
			message:
				"Debes guardar o cancelar tus cambios antes de cambiar de pestaña o salir.",
			variant: "warning",
		});
	};

	const canNavigateAwayWithUnsavedChanges = async () => {
		if (!hasUnsavedAccountChanges) return true;
		showUnsavedChangesWarning();
		return false;
	};

	const handleTabChange = async (nextTab: TabKey) => {
		if (nextTab === activeTab) return;
		const canNavigate = await canNavigateAwayWithUnsavedChanges();
		if (!canNavigate) return;
		setActiveTab(nextTab);
	};

	const handleAccountSectionChange = async (nextSection: AccountSectionKey) => {
		if (nextSection === activeAccountSection) return;
		const canNavigate = await canNavigateAwayWithUnsavedChanges();
		if (!canNavigate) return;
		setActiveAccountSection(nextSection);
	};

	const togglePersonalCategorySelection = (categoryId: number) => {
		setPersonalCategoryIds((prev) => {
			const validIds = sortedPersonalCategoryOptions.map((option) => option.id);
			const selectedIds = new Set(prev.filter((id) => validIds.includes(id)));

			if (selectedIds.has(categoryId)) {
				selectedIds.delete(categoryId);
			} else {
				selectedIds.add(categoryId);
			}

			return validIds.filter((id) => selectedIds.has(id));
		});
	};

	const toggleAllPersonalCategories = () => {
		setPersonalCategoryIds((prev) => {
			const availableIds = availablePersonalCategoryOptions.map(
				(option) => option.id,
			);
			if (availableIds.length === 0) return prev;

			const currentSet = new Set(prev);
			const shouldSelectAll = availableIds.some((id) => !currentSet.has(id));
			if (shouldSelectAll) {
				return [...new Set([...prev, ...availableIds])];
			}
			return prev.filter((id) => !availableIds.includes(id));
		});
	};

	const personalPhoneParts = splitPhoneWithCountryCode(personalForm.telefono ?? "");
	const personalNameParts = splitFullName(personalForm.nombre ?? "");
	const resolvedNameParts = splitFullName(resolvedProfile.nombre ?? "");
	const personalPhoneCountryCodeOptions = getPhoneCountryCodeOptions(
		personalPhoneParts.countryCode,
	);
	const paymentPhoneParts = splitPhoneWithCountryCode(
		paymentMethodForm.phoneNumber ?? "",
	);
	const paymentPhoneCountryCodeOptions = getPhoneCountryCodeOptions(
		paymentPhoneParts.countryCode,
	);

	const handleSavePersonal = async () => {
		const normalizedFirstName = personalNameParts.firstName.trim();
		const normalizedLastName = personalNameParts.lastName.trim();
		const normalizedPhone = composePhoneWithCountryCode(
			personalPhoneParts.countryCode,
			personalPhoneParts.localNumber,
		);
		const nextErrors: { nombre?: string; apellido?: string; telefono?: string } = {};

		if (normalizedFirstName.length < 2) {
			nextErrors.nombre = "El nombre debe tener al menos 2 caracteres";
		}

		if (normalizedLastName.length < 2) {
			nextErrors.apellido = "El apellido debe tener al menos 2 caracteres";
		}

		if (normalizedPhone) {
			const phoneError = getPhoneValidationError(normalizedPhone);
			if (phoneError) {
				nextErrors.telefono = phoneError;
			}
		}

		if (Object.keys(nextErrors).length > 0) {
			setPersonalFormErrors(nextErrors);
			return;
		}

		setPersonalFormErrors({});

		if (!onSavePersonalInfo) {
			showAlert({
				title: "Editar",
				message: "La edición de perfil estará disponible próximamente.",
				variant: "info",
			});
			return;
		}
		try {
			const saveResult = await onSavePersonalInfo({
				...personalForm,
				nombre: `${normalizedFirstName} ${normalizedLastName}`.trim(),
				firstName: normalizedFirstName,
				lastName: normalizedLastName,
				telefono: normalizedPhone,
				categoryIds: Array.from(new Set(personalCategoryIds)),
			});

			if (saveResult === false) {
				return;
			}

			setIsPersonalCategoriesDropdownOpen(false);
			setIsEditingPersonal(false);
		} catch {
			// Parent handler already surfaces the error via modal feedback.
		}
	};

	const openCreatePaymentMethodModal = () => {
		const firstOption = resolvedPaymentMethodOptions[0];
		setEditingPaymentMethodId(null);
		setPaymentMethodErrors({});
		setPaymentMethodForm({
			...PAYMENT_FORM_INITIAL,
			methodId: firstOption?.id ?? null,
			methodType: firstOption?.value ?? "",
			holderName:
				resolvedProfile.nombre && resolvedProfile.nombre !== "-"
					? resolvedProfile.nombre
					: "",
		});
		setIsPaymentMethodModalOpen(true);
	};

	const openEditPaymentMethodModal = (method: PaymentMethodRow) => {
		setEditingPaymentMethodId(method.id);
		setPaymentMethodErrors({});
		setPaymentMethodForm({
			methodId: method.methodId,
			methodType: method.methodType,
			bankName: method.bankName,
			accountNumber: method.accountNumber ?? "",
			phoneNumber: method.phoneNumber ?? "",
			identification: method.identification ?? "",
			holderName: method.holderName,
			isDefault: method.isDefault,
		});
		setIsPaymentMethodModalOpen(true);
	};

	const closePaymentMethodModal = () => {
		setIsPaymentMethodModalOpen(false);
		setEditingPaymentMethodId(null);
		setPaymentMethodErrors({});
	};

	const handleSavePaymentMethod = async () => {
		const nextErrors: {
			methodId?: string;
			methodType?: string;
			bankName?: string;
			accountNumber?: string;
			phoneNumber?: string;
			identification?: string;
			holderName?: string;
		} = {};

		const normalizedAccountNumber = paymentMethodForm.accountNumber.replace(
			/\D/g,
			"",
		);
		const normalizedPhoneNumber = paymentMethodForm.phoneNumber.trim();
		const normalizedIdentification = paymentMethodForm.identification.replace(
			/\D/g,
			"",
		);
		const normalizedBankName = paymentMethodForm.bankName.trim();
		const normalizedHolderName = paymentMethodForm.holderName.trim();

		if (!paymentMethodForm.methodType) {
			nextErrors.methodType = "Selecciona un método de pago";
		}

		const resolvedMethodId =
			(typeof paymentMethodForm.methodId === "number" &&
			paymentMethodForm.methodId > 0
				? paymentMethodForm.methodId
				: resolvedPaymentMethodOptions.find(
						(option) => option.value === paymentMethodForm.methodType,
					)?.id) ?? null;

		if (!resolvedMethodId) {
			nextErrors.methodId = "Selecciona un método de pago válido";
		}
		if (!normalizedBankName) {
			nextErrors.bankName = "Ingresa el banco";
		}

		if (paymentMethodForm.methodType === "transferencia_bancaria") {
			if (
				!normalizedAccountNumber ||
				normalizedAccountNumber.length < 8 ||
				normalizedAccountNumber.length > 20
			) {
				nextErrors.accountNumber =
					"Ingresa un número de cuenta válido (8 a 20 dígitos)";
			}
		}

		if (paymentMethodForm.methodType === "pago_movil") {
			const phoneError = getPhoneValidationError(normalizedPhoneNumber, {
				required: true,
			});
			if (phoneError) nextErrors.phoneNumber = phoneError;

			if (
				!normalizedIdentification ||
				normalizedIdentification.length < 6 ||
				normalizedIdentification.length > 10
			) {
				nextErrors.identification = "Ingresa una cédula válida (6 a 10 dígitos)";
			}
		}

		if (!normalizedHolderName) {
			nextErrors.holderName = "Ingresa el nombre del titular";
		}

		if (Object.keys(nextErrors).length > 0) {
			setPaymentMethodErrors(nextErrors);
			return;
		}

		const nextMethod: PaymentMethodSavePayload = {
			methodId: resolvedMethodId as number,
			methodType: paymentMethodForm.methodType as PaymentMethodType,
			bankName: normalizedBankName,
			accountNumber:
				paymentMethodForm.methodType === "transferencia_bancaria"
					? normalizedAccountNumber
					: "",
			phoneNumber:
				paymentMethodForm.methodType === "pago_movil"
					? normalizedPhoneNumber
					: "",
			identification:
				paymentMethodForm.methodType === "pago_movil"
					? normalizedIdentification
					: "",
			holderName: normalizedHolderName,
			isDefault: paymentMethodForm.isDefault,
		};

		try {
			if (editingPaymentMethodId) {
				if (!onUpdatePaymentMethod) {
					showAlert({
						title: "Editar",
						message:
							"La edición de métodos de pago estará disponible próximamente.",
						variant: "info",
					});
					return;
				}

				await onUpdatePaymentMethod(editingPaymentMethodId, nextMethod);
			} else {
				if (!onCreatePaymentMethod) {
					showAlert({
						title: "Agregar",
						message:
							"La creación de métodos de pago estará disponible próximamente.",
						variant: "info",
					});
					return;
				}

				await onCreatePaymentMethod(nextMethod);
			}

			closePaymentMethodModal();
		} catch (err) {
			showAlert({
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo guardar el método de pago.",
				variant: "error",
			});
		}
	};

	const handleDeletePaymentMethod = async (method: PaymentMethodRow) => {
		const confirmed = await requestConfirmation({
			title: "¿Eliminar método de pago?",
			message: "Esta acción no se puede deshacer.",
			variant: "danger",
			confirmLabel: "Eliminar",
			cancelLabel: "Cancelar",
		});

		if (!confirmed) return;

		if (!onDeletePaymentMethod) {
			showAlert({
				title: "Eliminar",
				message:
					"La eliminación de métodos de pago estará disponible próximamente.",
				variant: "info",
			});
			return;
		}

		try {
			await onDeletePaymentMethod(method.id);
		} catch (err) {
			showAlert({
				title: "Error",
				message:
					err instanceof Error
						? err.message
						: "No se pudo eliminar el método de pago.",
				variant: "error",
			});
		}
	};

	const resetProviderAddressSuggestions = () => {
		stateSuggestAbortRef.current?.abort();
		citySuggestAbortRef.current?.abort();
		if (stateSuggestDebounceRef.current) {
			window.clearTimeout(stateSuggestDebounceRef.current);
			stateSuggestDebounceRef.current = null;
		}
		if (citySuggestDebounceRef.current) {
			window.clearTimeout(citySuggestDebounceRef.current);
			citySuggestDebounceRef.current = null;
		}
		setStateSuggestions([]);
		setCitySuggestions([]);
		setStateSuggestStatus("idle");
		setCitySuggestStatus("idle");
	};

	const handleProviderStateInput = (nextValue: string) => {
		setProviderAddressForm((prev) => ({
			...prev,
			state: nextValue,
		}));
		setProviderAddressErrors((prev) => ({ ...prev, state: undefined }));

		stateSuggestAbortRef.current?.abort();
		if (stateSuggestDebounceRef.current) {
			window.clearTimeout(stateSuggestDebounceRef.current);
			stateSuggestDebounceRef.current = null;
		}

		setStateSuggestStatus("idle");
		const query = nextValue.trim();
		if (query.length < 2) {
			setStateSuggestions([]);
			return;
		}

		stateSuggestDebounceRef.current = window.setTimeout(async () => {
			setStateSuggestStatus("loading");
			const controller = new AbortController();
			stateSuggestAbortRef.current = controller;
			try {
				const results = await searchAdministrativeNominatim(query, {
					level: "state",
					country: "Venezuela",
					signal: controller.signal,
				});
				setStateSuggestions(results);
				setStateSuggestStatus("idle");
			} catch (error) {
				if (error instanceof DOMException && error.name === "AbortError") return;
				setStateSuggestions([]);
				setStateSuggestStatus("error");
			}
		}, 300);
	};

	const handleProviderCityInput = (nextValue: string) => {
		setProviderAddressForm((prev) => ({
			...prev,
			city: nextValue,
		}));
		setProviderAddressErrors((prev) => ({ ...prev, city: undefined }));

		citySuggestAbortRef.current?.abort();
		if (citySuggestDebounceRef.current) {
			window.clearTimeout(citySuggestDebounceRef.current);
			citySuggestDebounceRef.current = null;
		}

		setCitySuggestStatus("idle");
		const query = nextValue.trim();
		if (query.length < 2) {
			setCitySuggestions([]);
			return;
		}

		citySuggestDebounceRef.current = window.setTimeout(async () => {
			setCitySuggestStatus("loading");
			const controller = new AbortController();
			citySuggestAbortRef.current = controller;
			try {
				const results = await searchAdministrativeNominatim(query, {
					level: "city",
					stateContext: providerAddressForm.state,
					country: "Venezuela",
					signal: controller.signal,
				});
				setCitySuggestions(results);
				setCitySuggestStatus("idle");
			} catch (error) {
				if (error instanceof DOMException && error.name === "AbortError") return;
				setCitySuggestions([]);
				setCitySuggestStatus("error");
			}
		}, 300);
	};

	const selectStateSuggestion = (suggestion: AdministrativeSuggestion) => {
		stateSuggestAbortRef.current?.abort();
		if (stateSuggestDebounceRef.current) {
			window.clearTimeout(stateSuggestDebounceRef.current);
			stateSuggestDebounceRef.current = null;
		}
		setProviderAddressForm((prev) => ({ ...prev, state: suggestion.value }));
		setProviderAddressErrors((prev) => ({ ...prev, state: undefined }));
		setStateSuggestions([]);
		setStateSuggestStatus("idle");
	};

	const selectCitySuggestion = (suggestion: AdministrativeSuggestion) => {
		citySuggestAbortRef.current?.abort();
		if (citySuggestDebounceRef.current) {
			window.clearTimeout(citySuggestDebounceRef.current);
			citySuggestDebounceRef.current = null;
		}
		setProviderAddressForm((prev) => ({ ...prev, city: suggestion.value }));
		setProviderAddressErrors((prev) => ({ ...prev, city: undefined }));
		setCitySuggestions([]);
		setCitySuggestStatus("idle");
	};

	const openCreateProviderAddressModal = () => {
		resetProviderAddressSuggestions();
		setEditingProviderAddress(null);
		setProviderAddressForm({ state: "", city: "" });
		setProviderAddressErrors({});
		setIsProviderAddressModalOpen(true);
	};

	const openEditProviderAddressModal = (address: ProviderAddressRow) => {
		resetProviderAddressSuggestions();
		setEditingProviderAddress(address);
		setProviderAddressForm({
			state: address.state || "",
			city: address.city || "",
		});
		setProviderAddressErrors({});
		setIsProviderAddressModalOpen(true);
	};

	const closeProviderAddressModal = () => {
		resetProviderAddressSuggestions();
		setIsProviderAddressModalOpen(false);
		setEditingProviderAddress(null);
		setProviderAddressErrors({});
	};

	const handleSaveProviderAddress = async () => {
		const state = providerAddressForm.state.trim();
		const city = providerAddressForm.city.trim();
		const nextErrors: { state?: string; city?: string } = {};

		if (!state) nextErrors.state = "El estado es obligatorio";
		if (!city) nextErrors.city = "La ciudad es obligatoria";

		if (Object.keys(nextErrors).length > 0) {
			setProviderAddressErrors(nextErrors);
			return;
		}

		if (editingProviderAddress) {
			if (!onUpdateProviderAddress) {
				showAlert({
					title: "Direcciones",
					message: "La edición de direcciones estará disponible próximamente.",
					variant: "info",
				});
				return;
			}
			await onUpdateProviderAddress(editingProviderAddress.id, {
				state,
				city,
			});
		} else {
			if (!onCreateProviderAddress) {
				showAlert({
					title: "Direcciones",
					message: "La creación de direcciones estará disponible próximamente.",
					variant: "info",
				});
				return;
			}
			await onCreateProviderAddress({ state, city });
		}

		closeProviderAddressModal();
	};

	const handleToggleProviderAddress = async (address: ProviderAddressRow) => {
		if (!onToggleProviderAddress) {
			showAlert({
				title: "Direcciones",
				message:
					"La actualización de estado de direcciones estará disponible próximamente.",
				variant: "info",
			});
			return;
		}

		const shouldDeactivate = address.isActive;

		const confirmed = await requestConfirmation({
			title: shouldDeactivate ? "Desactivar dirección" : "Activar dirección",
			message: shouldDeactivate
				? "¿Deseas desactivar esta dirección? Podrás activarla nuevamente."
				: "¿Deseas activar nuevamente esta dirección?",
			variant: shouldDeactivate ? "danger" : "success",
			confirmLabel: shouldDeactivate ? "Sí, desactivar" : "Sí, activar",
			cancelLabel: "Cancelar",
		});

		if (!confirmed) return;
		try {
			await onToggleProviderAddress(address.id, !address.isActive);
		} catch (error) {
			const rawMessage =
				error instanceof Error
					? error.message
					: "No se pudo actualizar el estado de la dirección.";
			const isAddressInUse = rawMessage
				.toLowerCase()
				.includes("asociada a una solicitud o pedido");
			showAlert({
				title: isAddressInUse ? "Dirección en uso" : "Error",
				message: isAddressInUse
					? "No puedes desactivar esta dirección porque está asociada a una solicitud o pedido."
					: "No se pudo actualizar el estado de la dirección. Intenta de nuevo.",
				variant: isAddressInUse ? "warning" : "error",
			});
		}
	};

	/* ── schedule handlers ── */
	const handleStartEditSchedule = () => {
		refreshScheduleForm();
		setIsEditingSchedule(true);
	};

	const handleCancelEditSchedule = () => {
		refreshScheduleForm();
		setIsEditingSchedule(false);
	};

	const handleToggleDay = (day: DayAvailability["day"]) => {
		setScheduleValidationError(null);
		setScheduleForm((prev) =>
			prev.map((slot) =>
				slot.day === day ? { ...slot, enabled: !slot.enabled } : slot,
			),
		);
	};

	const handleScheduleChange = (
		day: DayAvailability["day"],
		field: "startTime" | "endTime" | "startPeriod" | "endPeriod",
		value: string,
	) => {
		setScheduleValidationError(null);
		setScheduleForm((prev) =>
			prev.map((slot) => {
				if (slot.day !== day) return slot;
				if (field === "startPeriod" || field === "endPeriod")
					return { ...slot, [field]: value as Period };
				return { ...slot, [field]: value };
			}),
		);
	};

	const handleSaveSchedule = async () => {
		const enabledRows = scheduleForm.filter((slot) => slot.enabled);
		if (enabledRows.length === 0) {
			setScheduleValidationError("Debes seleccionar al menos un día disponible.");
			return;
		}

		for (const slot of enabledRows) {
			const start = time12ToMinutes(slot.startTime, slot.startPeriod);
			const end = time12ToMinutes(slot.endTime, slot.endPeriod);
			if (start === null || end === null) {
				setScheduleValidationError(
					`Horario inválido en ${slot.day}. Usa formato 9:00 y AM/PM.`,
				);
				return;
			}
			if (start >= end) {
				setScheduleValidationError(
					`La hora de inicio debe ser menor a la hora fin en ${slot.day}.`,
				);
				return;
			}
		}

		if (!onSaveSchedule) {
			showAlert({
				title: "Editar",
				message: "La edición de horario estará disponible próximamente.",
				variant: "info",
			});
			return;
		}

		await onSaveSchedule(scheduleForm);
		setIsEditingSchedule(false);
	};

	/* ============================================================
	   TAB CONTENT RENDERERS
	   ============================================================ */

	/* ── TAB: Mis Solicitudes ── */
	const renderMisSolicitudes = () => {
		const pendientes = requests.filter(
			(order) => getSolicitudStatus(order) === "pendiente",
		).length;
		const aprobadas = requests.filter(
			(order) => getSolicitudStatus(order) === "aprobado",
		).length;
		const canceladas = requests.filter(
			(order) => getSolicitudStatus(order) === "cancelado",
		).length;

		const filteredOrders = requests.filter(
			(order) => getSolicitudStatus(order) === solicitudFilter,
		);

		if (requests.length === 0) {
			return (
				<div className='rounded-2xl border border-slate-100 bg-slate-50 p-8 text-center text-sm text-slate-500'>
					No tienes solicitudes registradas aún.
				</div>
			);
		}

		return (
			<div className='space-y-5'>
				<h2 className='text-2xl font-semibold text-gray-800'>Solicitudes</h2>

				<div className='rounded-2xl border border-slate-200 bg-white px-4 py-3'>
					<div className='grid grid-cols-3 divide-x divide-slate-200 text-center'>
						<button
							type='button'
							onClick={() => setSolicitudFilter("pendiente")}
							className='cursor-pointer py-1 transition hover:opacity-80'
						>
							<p
								className={`text-3xl font-bold ${solicitudFilter === "pendiente" ? "text-primary" : "text-slate-400"}`}
							>
								{pendientes}
							</p>
							<p className='text-sm text-slate-500'>Pendiente</p>
						</button>
						<button
							type='button'
							onClick={() => setSolicitudFilter("aprobado")}
							className='cursor-pointer py-1 transition hover:opacity-80'
						>
							<p
								className={`text-3xl font-bold ${solicitudFilter === "aprobado" ? "text-success-text" : "text-slate-400"}`}
							>
								{aprobadas}
							</p>
							<p className='text-sm text-slate-500'>Aprobado</p>
						</button>
						<button
							type='button'
							onClick={() => setSolicitudFilter("cancelado")}
							className='cursor-pointer py-1 transition hover:opacity-80'
						>
							<p
								className={`text-3xl font-bold ${solicitudFilter === "cancelado" ? "text-error-text" : "text-slate-400"}`}
							>
								{canceladas}
							</p>
							<p className='text-sm text-slate-500'>Cancelado</p>
						</button>
					</div>
				</div>

				<div className='space-y-4'>
					{filteredOrders.length === 0 ? (
						<div className='rounded-2xl border border-slate-100 bg-slate-50 p-8 text-center text-sm text-slate-500'>
							No hay solicitudes en estado {solicitudFilter}.
						</div>
					) : null}
					{filteredOrders.map((order, index) => {
						const isExpanded = expandedSolicitudIds.includes(order.id);
						const isUrgent = /urgente|urgent/i.test(
							String(order.urgency ?? ""),
						);
						const urgencyBadgeClass = isUrgent
							? "bg-red-50 text-red-500"
							: "bg-green-50 text-green-600";
						const parsedDescription = parseStructuredRequestDescription(
							order.description,
						);
						const items =
							order.requestItems ??
							(parsedDescription?.items ?? [])
								.map((item) => {
									const quantity = Number(item.quantity ?? 0);
									const unitPrice = Number(item.unitPrice ?? 0);
									const lineTotal = Number(
										item.lineTotal ?? quantity * unitPrice,
									);
									const name = item.name?.trim() || "Servicio";
									if (!Number.isFinite(quantity) || quantity <= 0)
										return null;
									return {
										name,
										quantity,
										unitPrice,
										lineTotal,
									};
								})
								.filter(
									(
										item,
									): item is {
										name: string;
										quantity: number;
										unitPrice: number;
										lineTotal: number;
									} => Boolean(item),
								);
						const requestDateLabel =
							order.requestDate || parsedDescription?.schedule?.date;
						const requestTimeLabel =
							order.requestTime || parsedDescription?.schedule?.time;

						return (
							<article
								key={`${order.id}-${index}`}
								className='rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'
							>
								<div className='mb-3 flex items-start justify-between gap-3'>
									<div className='flex items-center gap-3'>
										<div className='flex h-11 w-11 items-center justify-center rounded-full bg-secondary text-primary'>
											<User className='h-5 w-5' />
										</div>
										<div>
											<p className='text-lg font-semibold text-slate-800'>
												{order.cliente}
											</p>
											<p className='text-xs text-slate-400'>
												{order.fecha}
											</p>
										</div>
									</div>

									<div className='flex items-center gap-2'>
										<span
											className={`rounded-full px-3 py-1 text-xs font-semibold ${urgencyBadgeClass}`}
										>
											{isUrgent ? "Urgente" : "Normal"}
										</span>
									</div>
								</div>

								{isExpanded ? (
									<div className='mb-3 grid gap-3 border-y border-slate-200 py-3 text-sm text-slate-600 md:grid-cols-2'>
										<div className='flex items-start gap-2'>
											<MapPin className='mt-0.5 h-4 w-4 text-slate-400' />
											<span>
												{order.ubicacion ??
													"Sin dirección registrada"}
											</span>
										</div>
										<div className='flex items-start gap-2'>
											<Clock3 className='mt-0.5 h-4 w-4 text-slate-400' />
											<span>
												{requestDateLabel || order.fecha}
												{requestTimeLabel
													? ` · ${requestTimeLabel}`
													: ""}
											</span>
										</div>
									</div>
								) : null}

								<div className='mb-3 rounded-xl border border-slate-200'>
									<button
										type='button'
										onClick={() =>
											setExpandedSolicitudIds((prev) =>
												prev.includes(order.id)
													? prev.filter((id) => id !== order.id)
													: [...prev, order.id],
											)
										}
										className='flex cursor-pointer w-full items-center justify-between border-b border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700'
									>
										<div className='inline-flex items-center gap-2'>
											<CheckCircle2 className='h-4 w-4 text-primary' />
											Detalles del Pedido
										</div>
										{isExpanded ? (
											<ChevronUp className='h-4 w-4 text-slate-500' />
										) : (
											<ChevronDown className='h-4 w-4 text-slate-500' />
										)}
									</button>
									{isExpanded ? (
										<div className='space-y-2 px-4 py-3 text-sm'>
											<p className='font-medium text-slate-800'>
												{order.servicio}
											</p>
											{items.length > 0 ? (
												<div className='space-y-2'>
													{items.map((item, itemIndex) => (
														<div
															key={`${order.id}-item-${itemIndex}`}
															className='flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2'
														>
															<div>
																<p className='font-medium text-slate-700'>
																	{item.name}
																</p>
																<p className='text-xs text-slate-500'>
																	Cantidad:{" "}
																	{item.quantity} · $
																	{item.unitPrice.toFixed(
																		2,
																	)}
																</p>
															</div>
															<p className='font-semibold text-slate-700'>
																$
																{item.lineTotal.toFixed(
																	2,
																)}
															</p>
														</div>
													))}
												</div>
											) : (
												<p className='text-slate-500'>
													{order.description &&
													!order.description.startsWith(
														"SERVIX_REQUEST_V1::",
													)
														? order.description
														: "Solicitud de servicio"}
												</p>
											)}
											<div className='pt-1 text-right text-base font-semibold text-slate-700'>
												{order.total}
											</div>
										</div>
									) : null}
								</div>

								<div className='mb-3 rounded-xl border border-slate-200 px-4 py-3'>
									<div className='flex items-center justify-between text-sm'>
										<span className='text-slate-500'>Subtotal:</span>
										<span className='font-semibold text-slate-700'>
											{order.total}
										</span>
									</div>
									<div className='mt-2 flex items-center justify-between'>
										<span className='text-base font-semibold text-slate-700'>
											Total:
										</span>
										<span className='text-2xl font-bold text-primary'>
											{order.total}
										</span>
									</div>
								</div>

								<div className='flex items-center gap-2'>
									<button
										type='button'
										onClick={() => void onRejectSolicitud?.(order)}
										className='h-11 flex-1 rounded-xl border border-error-text cursor-pointer bg-white text-sm font-semibold text-error-text transition hover:bg-red-50'
									>
										<CircleX className='mr-1 inline h-4 w-4' />
										Rechazar
									</button>
									<button
										type='button'
										onClick={() => {
											void handleTabChange("pedidos");
											void onAcceptSolicitud?.(order);
										}}
										className='h-11 flex-1 rounded-xl bg-primary text-sm font-semibold cursor-pointer text-white transition hover:bg-primary-hover'
									>
										<CheckCircle2 className='mr-1 inline h-4 w-4' />
										Aceptar
									</button>
								</div>
							</article>
						);
					})}
				</div>
			</div>
		);
	};

	/* ── TAB: Datos Personales ── */
	const renderDatosPersonales = () => (
		<div className='space-y-8'>
			{/* ── Información Personal ── */}
			<section>
				<div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
					<div>
						<h2 className='text-2xl font-semibold text-gray-800'>
							Información Personal
						</h2>
						<p className='text-sm text-gray-500'>
							Actualiza tu información personal y profesional
						</p>
					</div>
					<div className='flex items-center gap-2'>
						{!isEditingPersonal ? (
							<button
								type='button'
								onClick={handleStartEditPersonal}
								className='cursor-pointer inline-flex h-10 items-center justify-center gap-2 self-start rounded-xl bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover'
							>
								<Pencil className='h-4 w-4' />
								Editar
							</button>
						) : (
							<>
								<button
									type='button'
									onClick={handleCancelEditPersonal}
									className='cursor-pointer h-10 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50'
								>
									Cancelar
								</button>
								<button
									type='button'
									disabled={isSavingPersonalInfo}
									onClick={() => void handleSavePersonal()}
									className='cursor-pointer h-10 rounded-xl bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60'
								>
									{isSavingPersonalInfo ? "Guardando..." : "Guardar"}
								</button>
							</>
						)}
					</div>
				</div>

				<div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
					<div>
						<FieldLabel icon={<User className='h-4 w-4' />} label='Nombre' />
						<input
							type='text'
							aria-label='Nombre'
							title='Nombre'
							readOnly={!isEditingPersonal}
							value={
								isEditingPersonal
									? personalNameParts.firstName
									: resolvedNameParts.firstName || "-"
							}
							onChange={(e) => {
								const nextFirstName = e.target.value;
								const nextFullName =
									`${nextFirstName} ${personalNameParts.lastName}`.trim();
								setPersonalForm((prev) => ({
									...prev,
									nombre: nextFullName,
								}));
								if (personalFormErrors.nombre) {
									setPersonalFormErrors((prev) => ({
										...prev,
										nombre: undefined,
									}));
								}
							}}
							className={
								"h-11 w-full rounded-xl border px-4 text-sm outline-none " +
								(isEditingPersonal
									? personalFormErrors.nombre
										? "border-red-400 bg-white text-slate-700 focus:border-red-400"
										: "border-slate-300 bg-white text-slate-700 focus:border-primary"
									: "border-gray-200 bg-gray-50 text-gray-700")
							}
						/>
						{personalFormErrors.nombre ? (
							<p className='mt-1 text-xs font-medium text-red-600'>
								{personalFormErrors.nombre}
							</p>
						) : null}
					</div>
					<div>
						<FieldLabel
							icon={<User className='h-4 w-4' />}
							label='Apellido'
						/>
						<input
							type='text'
							aria-label='Apellido'
							title='Apellido'
							readOnly={!isEditingPersonal}
							value={
								isEditingPersonal
									? personalNameParts.lastName
									: resolvedNameParts.lastName || "-"
							}
							onChange={(e) => {
								const nextLastName = e.target.value;
								const nextFullName =
									`${personalNameParts.firstName} ${nextLastName}`.trim();
								setPersonalForm((prev) => ({
									...prev,
									nombre: nextFullName,
								}));
								if (personalFormErrors.apellido) {
									setPersonalFormErrors((prev) => ({
										...prev,
										apellido: undefined,
									}));
								}
							}}
							className={
								"h-11 w-full rounded-xl border px-4 text-sm outline-none " +
								(isEditingPersonal
									? personalFormErrors.apellido
										? "border-red-400 bg-white text-slate-700 focus:border-red-400"
										: "border-slate-300 bg-white text-slate-700 focus:border-primary"
									: "border-gray-200 bg-gray-50 text-gray-700")
							}
						/>
						{personalFormErrors.apellido ? (
							<p className='mt-1 text-xs font-medium text-red-600'>
								{personalFormErrors.apellido}
							</p>
						) : null}
					</div>
					<div>
						<FieldLabel
							icon={<Briefcase className='h-4 w-4' />}
							label='Company name'
						/>
						<input
							type='text'
							aria-label='Company name'
							title='Company name'
							readOnly={!isEditingPersonal}
							value={
								isEditingPersonal
									? personalForm.companyName ||
										personalForm.oficio ||
										""
									: resolvedProfile.companyName ||
										resolvedProfile.oficio ||
										"-"
							}
							onChange={(e) =>
								setPersonalForm((prev) => ({
									...prev,
									companyName: e.target.value,
									oficio: e.target.value,
								}))
							}
							className={
								"h-11 w-full rounded-xl border px-4 text-sm outline-none " +
								(isEditingPersonal
									? "border-slate-300 bg-white text-slate-700 focus:border-primary"
									: "border-gray-200 bg-gray-50 text-gray-700")
							}
						/>
					</div>
					<div>
						<FieldLabel
							icon={<Mail className='h-4 w-4' />}
							label='Correo Electrónico'
						/>
						<input
							type='email'
							aria-label='Correo Electrónico'
							title='Correo Electrónico'
							readOnly={!isEditingPersonal}
							value={
								isEditingPersonal
									? personalForm.correo || ""
									: resolvedProfile.correo || "-"
							}
							onChange={(e) => {
								setPersonalForm((prev) => ({
									...prev,
									correo: e.target.value,
								}));
								if (personalFormErrors.correo) {
									setPersonalFormErrors((prev) => ({
										...prev,
										correo: undefined,
									}));
								}
							}}
							className={
								"h-11 w-full rounded-xl border px-4 text-sm outline-none " +
								(isEditingPersonal
									? personalFormErrors.correo
										? "border-red-400 bg-white text-slate-700 focus:border-red-400"
										: "border-slate-300 bg-white text-slate-700 focus:border-primary"
									: "border-gray-200 bg-gray-50 text-gray-700")
							}
						/>
						{personalFormErrors.correo ? (
							<p className='mt-1 text-xs font-medium text-red-600'>
								{personalFormErrors.correo}
							</p>
						) : null}
					</div>
					<div>
						<FieldLabel
							icon={<Phone className='h-4 w-4' />}
							label='Teléfono'
						/>
						{isEditingPersonal ? (
							<div className='grid grid-cols-[140px_1fr] gap-2 sm:grid-cols-[180px_1fr]'>
								<select
									value={personalPhoneParts.countryCode}
									onChange={(e) => {
										setPersonalForm((prev) => ({
											...prev,
											telefono: composePhoneWithCountryCode(
												e.target.value,
												personalPhoneParts.localNumber,
											),
										}));
										if (personalFormErrors.telefono) {
											setPersonalFormErrors((prev) => ({
												...prev,
												telefono: undefined,
											}));
										}
									}}
									className='h-11 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-700 outline-none focus:border-primary'
								>
									{personalPhoneCountryCodeOptions.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
								<input
									type='tel'
									aria-label='Teléfono'
									title='Teléfono'
									inputMode='tel'
									pattern='^\+?[0-9()\s-]{7,20}$'
									value={personalPhoneParts.localNumber}
									onChange={(e) => {
										const nextPhoneParts = splitPhoneInputValue(
											e.target.value,
											personalPhoneParts.countryCode,
											{
												inferCallingCodeFromPlainDigits: false,
												codeOnlyMeansEmptyLocal: false,
											},
										);
										setPersonalForm((prev) => ({
											...prev,
											telefono: composePhoneWithCountryCode(
												nextPhoneParts.countryCode,
												nextPhoneParts.localNumber,
											),
										}));
										if (personalFormErrors.telefono) {
											setPersonalFormErrors((prev) => ({
												...prev,
												telefono: undefined,
											}));
										}
									}}
									className='h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none focus:border-primary'
								/>
							</div>
						) : (
							<input
								type='text'
								aria-label='Teléfono'
								title='Teléfono'
								readOnly
								value={resolvedProfile.telefono || "-"}
								className='h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-700 outline-none'
							/>
						)}
						{isEditingPersonal && personalFormErrors.telefono ? (
							<p className='mt-1 text-xs font-medium text-red-600'>
								{personalFormErrors.telefono}
							</p>
						) : null}
					</div>
					<div>
						<FieldLabel
							icon={<FileUser className='h-4 w-4' />}
							label='Años de Experiencia'
						/>
						<input
							type='number'
							min={0}
							max={100}
							inputMode='numeric'
							aria-label='Años de Experiencia'
							title='Años de Experiencia'
							readOnly={!isEditingPersonal}
							value={(isEditingPersonal
								? personalForm.experiencia || ""
								: resolvedProfile.experiencia || ""
							).replace(/[^\d]/g, "")}
							onChange={(e) => {
								setPersonalForm((prev) => ({
									...prev,
									experiencia: e.target.value.replace(/[^\d]/g, ""),
								}));
								if (personalFormErrors.experiencia) {
									setPersonalFormErrors((prev) => ({
										...prev,
										experiencia: undefined,
									}));
								}
							}}
							className={
								"h-11 w-full rounded-xl border px-4 text-sm outline-none " +
								(isEditingPersonal
									? personalFormErrors.experiencia
										? "border-red-400 bg-white text-slate-700 focus:border-red-400"
										: "border-slate-300 bg-white text-slate-700 focus:border-primary"
									: "border-gray-200 bg-gray-50 text-gray-700")
							}
						/>
						{personalFormErrors.experiencia ? (
							<p className='mt-1 text-xs font-medium text-red-600'>
								{personalFormErrors.experiencia}
							</p>
						) : null}
					</div>
					<div>
						<FieldLabel
							icon={<Languages className='h-4 w-4' />}
							label='Idiomas'
						/>
						<input
							type='text'
							aria-label='Idiomas'
							title='Idiomas'
							readOnly={!isEditingPersonal}
							value={
								isEditingPersonal
									? personalForm.idiomas || ""
									: resolvedProfile.idiomas || "-"
							}
							onChange={(e) =>
								setPersonalForm((prev) => ({
									...prev,
									idiomas: e.target.value,
								}))
							}
							className={
								"h-11 w-full rounded-xl border px-4 text-sm outline-none " +
								(isEditingPersonal
									? "border-slate-300 bg-white text-slate-700 focus:border-primary"
									: "border-gray-200 bg-gray-50 text-gray-700")
							}
						/>
					</div>

					{/* Fecha de Nacimiento */}
					<div>
						<FieldLabel
							icon={<Calendar className='h-4 w-4' />}
							label="Fecha de Nacimiento (opcional)"
						/>
						<input
							type={isEditingPersonal ? "date" : "text"}
							aria-label='Fecha de Nacimiento'
							title='Fecha de Nacimiento'
							readOnly={!isEditingPersonal}
							value={
								isEditingPersonal
									? (personalForm.fechaNacimiento ?? "")
									: (resolvedProfile.fechaNacimiento
										? (() => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(resolvedProfile.fechaNacimiento!); return m ? `${m[3]}/${m[2]}/${m[1]}` : resolvedProfile.fechaNacimiento!; })()
										: "-")
							}
							onChange={(e) =>
								setPersonalForm((prev) => ({
									...prev,
									fechaNacimiento: e.target.value || null,
								}))
							}
							className={
								"h-11 w-full rounded-xl border px-4 text-sm outline-none " +
								(isEditingPersonal
									? "border-slate-300 bg-white text-slate-700 focus:border-primary"
									: "border-gray-200 bg-gray-50 text-gray-700")
							}
						/>
					</div>

					{/* Género */}
					<div>
						<FieldLabel
							icon={<User className='h-4 w-4' />}
							label="Género (opcional)"
						/>
						{isEditingPersonal ? (
							<select
								aria-label='Género'
								title='Género'
								value={personalForm.genero ?? ""}
								onChange={(e) =>
									setPersonalForm((prev) => ({
										...prev,
										genero: e.target.value || null,
									}))
								}
								className='h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none focus:border-primary'
							>
								<option value=''>Sin especificar</option>
								<option value='Masculino'>Masculino</option>
								<option value='Femenino'>Femenino</option>
								<option value='Otro'>Prefiero no decirlo</option>
							</select>
						) : (
							<input
								type='text'
								aria-label='Género'
								title='Género'
								readOnly
								value={resolvedProfile.genero ?? "-"}
								className='h-11 w-full rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-700 outline-none'
							/>
						)}
					</div>

					<div className='md:col-span-2'>
						<FieldLabel
							icon={<Grid3X3 className='h-4 w-4' />}
							label='Categorías del servicio'
						/>
						{isEditingPersonal ? (
							<div className='space-y-2'>
								<div
									ref={personalCategoriesDropdownRef}
									className='relative'
								>
									<div className='flex min-h-11 w-full items-start gap-2 rounded-xl border border-primary/20 bg-secondary/30 px-3 py-2'>
										<div className='flex flex-1 flex-wrap items-center gap-2'>
											{selectedPersonalCategoryLabels.length > 0 ? (
												selectedPersonalCategoryLabels.map(
													(label, index) => {
														const categoryId =
															orderedPersonalCategoryIds[
																index
															];
														return (
															<span
																key={`${categoryId}-${label}`}
																className='inline-flex items-center gap-1 rounded-md border border-primary/20 bg-secondary px-2.5 py-1 text-xs font-medium text-primary'
															>
																{label}
																<button
																	type='button'
																	onClick={() =>
																		togglePersonalCategorySelection(
																			categoryId,
																		)
																	}
																	className='cursor-pointer rounded p-0.5 text-primary/70 transition hover:bg-secondary-hover hover:text-primary'
																	aria-label={`Quitar ${label}`}
																	title={`Quitar ${label}`}
																>
																	<X className='h-3 w-3' />
																</button>
															</span>
														);
													},
												)
											) : (
												<span className='py-1 text-sm text-primary/60'>
													Selecciona una o varias categorías
												</span>
											)}
										</div>
										<button
											type='button'
											onClick={() =>
												setIsPersonalCategoriesDropdownOpen(
													(prev) => !prev,
												)
											}
											className='cursor-pointer rounded p-1 text-primary transition hover:bg-secondary'
											aria-label='Mostrar categorías'
											title='Mostrar categorías'
										>
											<ChevronDown
												className={`h-5 w-5 transition-transform ${isPersonalCategoriesDropdownOpen ? "rotate-180" : ""}`}
											/>
										</button>
									</div>

									{isPersonalCategoriesDropdownOpen ? (
										<div className='absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-primary/20 bg-white shadow-lg'>
											<button
												type='button'
												onClick={toggleAllPersonalCategories}
												className='cursor-pointer flex w-full items-center gap-2 border-b border-primary/10 px-3 py-2 text-left text-sm text-slate-700 hover:bg-secondary/40'
											>
												<span
													className={
														"inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] " +
														(areAllPersonalCategoriesSelected
															? "border-primary bg-primary text-white"
															: "border-slate-300 bg-white text-transparent")
													}
												>
													✓
												</span>
												<span className='font-medium'>
													Seleccionar todas
												</span>
											</button>

											<div className='max-h-56 overflow-auto py-1'>
												{availablePersonalCategoryOptions.length ===
												0 ? (
													<p className='px-3 py-2 text-sm text-slate-500'>
														No hay categorías disponibles
													</p>
												) : (
													availablePersonalCategoryOptions.map(
														(category) => {
															const selected =
																selectedPersonalCategoryIdSet.has(
																	category.id,
																);
															return (
																<button
																	key={category.id}
																	type='button'
																	onClick={() =>
																		togglePersonalCategorySelection(
																			category.id,
																		)
																	}
																	className='cursor-pointer flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-secondary/40'
																>
																	<span
																		className={
																			"inline-flex h-4 w-4 items-center justify-center rounded border text-[10px] " +
																			(selected
																				? "border-primary bg-primary text-white"
																				: "border-slate-300 bg-white text-transparent")
																		}
																	>
																		✓
																	</span>
																	<span>
																		{category.label}
																	</span>
																</button>
															);
														},
													)
												)}
											</div>
										</div>
									) : null}
								</div>

								<p className='text-xs text-slate-500'>
									Selecciona una o varias categorías del servicio.
								</p>
							</div>
						) : selectedPersonalCategoryLabels.length > 0 ? (
							<div className='flex min-h-11 flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2'>
								{selectedPersonalCategoryLabels.map((label, index) => (
									<span
										key={`${label}-${index}`}
										className='rounded-full border border-primary/25 bg-primary/5 px-3 py-0.5 text-xs font-medium text-primary'
									>
										{label}
									</span>
								))}
							</div>
						) : (
							<div className='flex h-11 items-center rounded-xl border border-gray-200 bg-gray-50 px-4 text-sm text-gray-700'>
								Sin categorías asignadas
							</div>
						)}
					</div>
					<div className='md:col-span-2'>
						<FieldLabel
							icon={<Briefcase className='h-4 w-4' />}
							label='Biografía Profesional'
						/>
						<textarea
							aria-label='Biografía Profesional'
							title='Biografía Profesional'
							readOnly={!isEditingPersonal}
							value={
								isEditingPersonal
									? personalForm.biografia || ""
									: resolvedProfile.biografia ||
										"Sin biografía profesional"
							}
							onChange={(e) =>
								setPersonalForm((prev) => ({
									...prev,
									biografia: e.target.value,
								}))
							}
							className={
								"min-h-24 w-full resize-none rounded-xl border px-4 py-3 text-sm outline-none " +
								(isEditingPersonal
									? "border-slate-300 bg-white text-slate-700 focus:border-primary"
									: "border-gray-200 bg-gray-50 text-gray-700")
							}
						/>
					</div>
				</div>
			</section>

			<hr className='border-gray-200' />

			{/* ── Insignias Obtenidas ── */}
			<section>
				<div className='mb-4 flex items-center gap-2'>
					<Award className='h-5 w-5 text-primary' />
					<h3 className='text-xl font-semibold text-gray-800'>
						Insignias Obtenidas
					</h3>
				</div>
				{badges.length > 0 ? (
					<div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
						{badges.map((badge) => (
							<div
								key={badge.id}
								className='rounded-2xl border border-primary/15 bg-primary/5 px-4 py-3'
							>
								<div className='flex items-center gap-3'>
									<div className='h-9 w-9 overflow-hidden rounded-lg border border-primary/20 bg-white p-1.5'>
										<BadgeIcon
											src={getImageUri(badge.icon) || badge.icon}
											type={badge.type}
											alt={badge.name}
											className='h-full w-full object-contain'
										/>
									</div>
									<div className='min-w-0'>
										<p className='truncate text-sm font-semibold text-primary'>
											{badge.name}
										</p>
										{badge.description?.trim() ? (
											<p className='mt-0.5 text-xs text-slate-600 line-clamp-2'>
												{badge.description}
											</p>
										) : null}
									</div>
								</div>
							</div>
						))}
					</div>
				) : (
					<div className='rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-sm text-slate-500'>
						Aún no tienes insignias asignadas.
					</div>
				)}
			</section>

			<hr className='border-gray-200' />

			{/* ── Horario de Disponibilidad ── */}
			<section>
				<div className='mb-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
					<div className='flex items-center gap-2'>
						<Clock3 className='h-5 w-5 text-primary' />
						<h3 className='text-xl font-semibold text-gray-800'>
							Horario de Disponibilidad
						</h3>
					</div>
					<div className='flex items-center gap-2'>
						{!isEditingSchedule ? (
							<button
								type='button'
								onClick={handleStartEditSchedule}
								className='cursor-pointer inline-flex h-10 items-center justify-center gap-2 self-start rounded-xl bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover'
							>
								<Pencil className='h-4 w-4' />
								Editar
							</button>
						) : (
							<>
								<button
									type='button'
									onClick={handleCancelEditSchedule}
									className='cursor-pointer h-10 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50'
								>
									Cancelar
								</button>
								<button
									type='button'
									disabled={isSavingSchedule}
									onClick={() => void handleSaveSchedule()}
									className='cursor-pointer h-10 rounded-xl bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60'
								>
									{isSavingSchedule ? "Guardando..." : "Guardar"}
								</button>
							</>
						)}
					</div>
				</div>

				{!isEditingSchedule ? (
					<div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
						{sortedSchedule.map((slot) => (
							<ScheduleRow
								key={slot.day}
								day={slot.day}
								hours={formatScheduleToRange(slot)}
							/>
						))}
					</div>
				) : (
					<>
						<AvailabilityScheduleEditor
							availability={sortedSchedule}
							onToggleDay={handleToggleDay}
							onChangeSchedule={handleScheduleChange}
						/>
						{scheduleValidationError ? (
							<div className='mt-3 rounded-xl bg-error-text px-4 py-2.5 text-sm font-medium text-white'>
								Verifica la informacion suministrada, recuerda guardarla
							</div>
						) : null}
					</>
				)}
			</section>
		</div>
	);

	/* ── TAB: Métodos de Pago ── */
	const renderMetodosPago = () => (
		<>
			<div className='space-y-6'>
				<div>
					<h2 className='text-2xl font-semibold text-gray-800'>
						Métodos de Pago
					</h2>
				</div>

				<div className='flex items-start gap-3 rounded-2xl border border-cyan-100 bg-cyan-50/60 px-4 py-4'>
					<div className='mt-0.5 flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-100 text-primary'>
						<CreditCard className='h-5 w-5' />
					</div>
					<div>
						<p className='text-sm font-semibold text-slate-800'>
							Configura cómo recibir pagos
						</p>
						<p className='text-sm text-slate-500'>
							Agrega tus métodos de pago para recibir el dinero de tus
							servicios. Marca uno como principal para usarlo por defecto.
						</p>
					</div>
				</div>

				<div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
					<h3 className='text-2xl font-semibold text-slate-800'>
						Tus Métodos de Pago ({paymentMethods.length})
					</h3>
					<button
						type='button'
						onClick={openCreatePaymentMethodModal}
						disabled={isSavingPaymentMethod}
						className='cursor-pointer inline-flex h-11 items-center justify-center gap-2 self-start rounded-full bg-primary px-5 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
					>
						<Plus className='h-4 w-4' />
						Agregar
					</button>
				</div>

				{paymentMethods.length === 0 ? (
					<div className='rounded-2xl border border-slate-200 bg-slate-50 px-6 py-8 text-center text-sm text-slate-500'>
						No tienes métodos de pago registrados.
					</div>
				) : (
					<div className='grid grid-cols-1 gap-5 lg:grid-cols-2'>
						{paymentMethods.map((method) => (
							<article
								key={method.id}
								className='rounded-2xl border border-slate-300 bg-white px-5 py-4 shadow-sm'
							>
								<div className='mb-2 flex items-start justify-between gap-3'>
									<h4 className='text-base font-semibold text-slate-800'>
										{method.bankName}
									</h4>
									{method.isDefault ? (
										<span className='inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary'>
											<Star className='h-3 w-3' />
											Principal
										</span>
									) : null}
								</div>
								<p className='text-sm text-slate-500'>
									{method.methodType === "transferencia_bancaria"
										? `Cuenta ${maskAccountNumber(method.accountNumber ?? "")}`
										: `Pago móvil ${method.phoneNumber ?? "-"} · CI ${method.identification ?? "-"}`}
									{" - "}
									{method.holderName}
								</p>
								<p className='mt-1 text-xs text-slate-400'>
									{toPaymentMethodLabel(method.methodType)}
								</p>

								<div className='mt-4 flex items-center gap-1'>
									<button
										type='button'
										onClick={() => openEditPaymentMethodModal(method)}
										disabled={isSavingPaymentMethod}
										className='cursor-pointer rounded-lg p-2 text-primary transition hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-50'
										aria-label='Editar método de pago'
									>
										<Pencil className='h-4 w-4' />
									</button>
									<button
										type='button'
										onClick={() => {
											void handleDeletePaymentMethod(method);
										}}
										disabled={isSavingPaymentMethod}
										className='cursor-pointer rounded-lg p-2 text-red-500 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50'
										aria-label='Eliminar método de pago'
									>
										<Trash2 className='h-4 w-4' />
									</button>
								</div>
							</article>
						))}
					</div>
				)}
			</div>

			{isPaymentMethodModalOpen ? (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6'>
					<div className='relative flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white'>
						<div className='flex items-center justify-between border-b border-slate-200 px-6 py-4'>
							<h3 className='text-2xl font-semibold text-slate-800'>
								{editingPaymentMethodId
									? "Editar Método de Pago"
									: "Agregar Método de Pago"}
							</h3>
							<button
								type='button'
								onClick={closePaymentMethodModal}
								disabled={isSavingPaymentMethod}
								className='cursor-pointer rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50'
								aria-label='Cerrar modal de método de pago'
							>
								<X className='h-5 w-5' />
							</button>
						</div>

						<div className='flex-1 space-y-5 overflow-y-auto px-6 py-6'>
							<div>
								<label className='mb-2 block text-sm font-semibold text-slate-700'>
									Tipo de Método de Pago
								</label>
								<div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
									{resolvedPaymentMethodOptions.map((option) => {
										const isActive =
											paymentMethodForm.methodType === option.value;
										return (
											<button
												key={option.value}
												type='button'
												onClick={() => {
													setPaymentMethodForm((prev) => ({
														...prev,
														methodId: option.id,
														methodType: option.value,
														accountNumber:
															option.value ===
															"transferencia_bancaria"
																? prev.accountNumber
																: "",
														phoneNumber:
															option.value === "pago_movil"
																? prev.phoneNumber
																: "",
														identification:
															option.value === "pago_movil"
																? prev.identification
																: "",
													}));
													setPaymentMethodErrors((prev) => ({
														...prev,
														methodId: undefined,
														methodType: undefined,
														accountNumber: undefined,
														phoneNumber: undefined,
														identification: undefined,
													}));
												}}
												className={
													"cursor-pointer rounded-xl border px-4 py-3 text-left transition " +
													(isActive
														? "border-primary bg-cyan-100 text-primary"
														: "border-slate-300 bg-white text-slate-600 hover:border-primary/40")
												}
											>
												<p className='text-sm font-semibold'>
													{option.label}
												</p>
												<p className='text-xs text-slate-500'>
													{option.description}
												</p>
											</button>
										);
									})}
								</div>
								{paymentMethodErrors.methodType ? (
									<p className='mt-1 text-xs font-medium text-red-600'>
										{paymentMethodErrors.methodType}
									</p>
								) : null}
								{paymentMethodErrors.methodId ? (
									<p className='mt-1 text-xs font-medium text-red-600'>
										{paymentMethodErrors.methodId}
									</p>
								) : null}
							</div>

							<div>
								<label className='mb-2 block text-sm font-semibold text-slate-700'>
									Banco *
								</label>
								<select
									aria-label='Banco'
									title='Banco'
									value={paymentMethodForm.bankName}
									onChange={(event) => {
										setPaymentMethodForm((prev) => ({
											...prev,
											bankName: event.target.value,
										}));
										setPaymentMethodErrors((prev) => ({
											...prev,
											bankName: undefined,
										}));
									}}
									className='h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-primary'
								>
									<option value=''>Selecciona un banco</option>
									{paymentMethodForm.bankName &&
									!BANKS_OPTIONS.some(
										(bank) =>
											bank.label === paymentMethodForm.bankName,
									) ? (
										<option value={paymentMethodForm.bankName}>
											{paymentMethodForm.bankName}
										</option>
									) : null}
									{BANKS_OPTIONS.map((bank) => (
										<option key={bank.value} value={bank.label}>
											{bank.label}
										</option>
									))}
								</select>
								{paymentMethodErrors.bankName ? (
									<p className='mt-1 text-xs font-medium text-red-600'>
										{paymentMethodErrors.bankName}
									</p>
								) : null}
							</div>

							{paymentMethodForm.methodType === "transferencia_bancaria" ? (
								<div>
									<label className='mb-2 block text-sm font-semibold text-slate-700'>
										Número de Cuenta *
									</label>
									<input
										type='text'
										inputMode='numeric'
										value={paymentMethodForm.accountNumber}
										onChange={(event) => {
											setPaymentMethodForm((prev) => ({
												...prev,
												accountNumber: event.target.value.replace(
													/\D/g,
													"",
												),
											}));
											setPaymentMethodErrors((prev) => ({
												...prev,
												accountNumber: undefined,
											}));
										}}
										placeholder='Ej: 12345678901'
										className='h-11 w-full rounded-xl border border-slate-300 bg-slate-100/70 px-4 text-sm text-slate-700 outline-none transition focus:border-primary focus:bg-white'
									/>
									{paymentMethodErrors.accountNumber ? (
										<p className='mt-1 text-xs font-medium text-red-600'>
											{paymentMethodErrors.accountNumber}
										</p>
									) : null}
								</div>
							) : null}

							{paymentMethodForm.methodType === "pago_movil" ? (
								<>
									<div>
										<label className='mb-2 block text-sm font-semibold text-slate-700'>
											Teléfono *
										</label>
										<input
											type='text'
											inputMode='tel'
											value={paymentPhoneParts.localNumber}
											onChange={(event) => {
												const nextPhoneParts =
													splitPhoneInputValue(
														event.target.value,
														paymentPhoneParts.countryCode,
														{
															inferCallingCodeFromPlainDigits: false,
															codeOnlyMeansEmptyLocal: false,
														},
													);
												setPaymentMethodForm((prev) => ({
													...prev,
													phoneNumber:
														composePhoneWithCountryCode(
															nextPhoneParts.countryCode,
															nextPhoneParts.localNumber,
														),
												}));
												setPaymentMethodErrors((prev) => ({
													...prev,
													phoneNumber: undefined,
												}));
											}}
											placeholder='Ej: 4121234567'
											className='h-11 w-full rounded-xl border border-slate-300 bg-slate-100/70 px-4 text-sm text-slate-700 outline-none transition focus:border-primary focus:bg-white'
										/>
										<div className='mt-2'>
											<select
												value={paymentPhoneParts.countryCode}
												onChange={(event) => {
													setPaymentMethodForm((prev) => ({
														...prev,
														phoneNumber:
															composePhoneWithCountryCode(
																event.target.value,
																paymentPhoneParts.localNumber,
															),
													}));
													setPaymentMethodErrors((prev) => ({
														...prev,
														phoneNumber: undefined,
													}));
												}}
												className='h-11 w-full rounded-xl border border-slate-300 bg-slate-100/70 px-4 text-sm text-slate-700 outline-none transition focus:border-primary focus:bg-white'
											>
												{paymentPhoneCountryCodeOptions.map(
													(option) => (
														<option
															key={option.value}
															value={option.value}
														>
															{option.label}
														</option>
													),
												)}
											</select>
										</div>
										{paymentMethodErrors.phoneNumber ? (
											<p className='mt-1 text-xs font-medium text-red-600'>
												{paymentMethodErrors.phoneNumber}
											</p>
										) : null}
									</div>

									<div>
										<label className='mb-2 block text-sm font-semibold text-slate-700'>
											Cédula *
										</label>
										<input
											type='text'
											inputMode='numeric'
											value={paymentMethodForm.identification}
											onChange={(event) => {
												setPaymentMethodForm((prev) => ({
													...prev,
													identification:
														event.target.value.replace(
															/\D/g,
															"",
														),
												}));
												setPaymentMethodErrors((prev) => ({
													...prev,
													identification: undefined,
												}));
											}}
											placeholder='Ej: 12345678'
											className='h-11 w-full rounded-xl border border-slate-300 bg-slate-100/70 px-4 text-sm text-slate-700 outline-none transition focus:border-primary focus:bg-white'
										/>
										{paymentMethodErrors.identification ? (
											<p className='mt-1 text-xs font-medium text-red-600'>
												{paymentMethodErrors.identification}
											</p>
										) : null}
									</div>
								</>
							) : null}

							<div>
								<label className='mb-2 block text-sm font-semibold text-slate-700'>
									Nombre del Titular *
								</label>
								<input
									type='text'
									value={paymentMethodForm.holderName}
									onChange={(event) => {
										setPaymentMethodForm((prev) => ({
											...prev,
											holderName: event.target.value,
										}));
										setPaymentMethodErrors((prev) => ({
											...prev,
											holderName: undefined,
										}));
									}}
									placeholder='Ej: Juan Pérez García'
									className='h-11 w-full rounded-xl border border-slate-300 bg-slate-100/70 px-4 text-sm text-slate-700 outline-none transition focus:border-primary focus:bg-white'
								/>
								{paymentMethodErrors.holderName ? (
									<p className='mt-1 text-xs font-medium text-red-600'>
										{paymentMethodErrors.holderName}
									</p>
								) : null}
							</div>

							<label className='flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3'>
								<input
									type='checkbox'
									checked={paymentMethodForm.isDefault}
									onChange={(event) =>
										setPaymentMethodForm((prev) => ({
											...prev,
											isDefault: event.target.checked,
										}))
									}
									className='h-5 w-5 rounded border border-primary/40 text-primary focus:ring-primary'
								/>
								<span className='text-sm text-slate-700'>
									<span className='font-semibold'>
										Método predeterminado
									</span>{" "}
									Usar este método para recibir pagos por defecto
								</span>
							</label>

							<div className='grid grid-cols-1 gap-3 pt-2 sm:grid-cols-2'>
								<button
									type='button'
									onClick={closePaymentMethodModal}
									disabled={isSavingPaymentMethod}
									className='cursor-pointer h-11 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60'
								>
									Cancelar
								</button>
								<button
									type='button'
									onClick={() => {
										void handleSavePaymentMethod();
									}}
									disabled={isSavingPaymentMethod}
									className='cursor-pointer inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
								>
									<Save className='h-4 w-4' />
									{isSavingPaymentMethod
										? "Guardando..."
										: "Guardar Método"}
								</button>
							</div>
						</div>
					</div>
				</div>
			) : null}
		</>
	);

	/* ── TAB: Direcciones ── */
	const renderDirecciones = () => {
		const cards = providerAddresses.map((address, index) => ({
			key: address.id,
			title: `Dirección ${index + 1}`,
			isActive: address.isActive,
			rows: [
				{ label: "Estado", value: address.state || "-" },
				{ label: "Ciudad", value: address.city || "-" },
			],
			onEdit: () => openEditProviderAddressModal(address),
			onToggleActive: () => {
				void handleToggleProviderAddress(address);
			},
			toggleActiveLabel: "Desactivar",
			toggleInactiveLabel: "Activar",
		}));

		return (
			<>
				<AddressCardsBoard
					title='Direcciones guardadas'
					subtitle='Gestiona tus ubicaciones registradas por ciudad y estado'
					addLabel='Agregar dirección'
					onAdd={openCreateProviderAddressModal}
					actionsDisabled={isSavingAddress}
					cards={cards}
				/>

				{isProviderAddressModalOpen ? (
					<div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4'>
						<div className='w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl'>
							<div className='mb-4 flex items-center justify-between'>
								<h3 className='text-lg font-semibold text-slate-800'>
									{editingProviderAddress
										? "Editar dirección"
										: "Agregar dirección"}
								</h3>
								<button
									type='button'
									onClick={closeProviderAddressModal}
									className='cursor-pointer rounded-full p-1 text-slate-500 transition hover:bg-slate-100'
									aria-label='Cerrar modal de dirección'
								>
									<X className='h-4 w-4' />
								</button>
							</div>

							<div className='space-y-4'>
								<div>
									<label className='mb-1 block text-sm font-medium text-slate-700'>
										Estado
									</label>
									<div className='relative'>
										<input
											type='text'
											value={providerAddressForm.state}
											onChange={(e) =>
												handleProviderStateInput(e.target.value)
											}
											onBlur={() => {
												window.setTimeout(
													() => setStateSuggestions([]),
													150,
												);
											}}
											onKeyDown={(event) => {
												if (event.key === "Escape")
													setStateSuggestions([]);
											}}
											className='h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none focus:border-primary'
											placeholder='Ej: Miranda'
										/>
										{stateSuggestions.length > 0 ? (
											<ul className='absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-md'>
												{stateSuggestions.map((suggestion) => (
													<li
														key={`state-${suggestion.value}-${suggestion.label}`}
														className='border-b border-slate-100 last:border-b-0'
													>
														<button
															type='button'
															className='w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
															onMouseDown={(event) => {
																event.preventDefault();
																selectStateSuggestion(
																	suggestion,
																);
															}}
														>
															{suggestion.label}
														</button>
													</li>
												))}
											</ul>
										) : null}
									</div>
									{stateSuggestStatus === "loading" ? (
										<p className='mt-1 text-xs text-slate-500'>
											Buscando sugerencias...
										</p>
									) : null}
									{providerAddressErrors.state ? (
										<p className='mt-1 text-xs text-red-500'>
											{providerAddressErrors.state}
										</p>
									) : null}
								</div>

								<div>
									<label className='mb-1 block text-sm font-medium text-slate-700'>
										Ciudad
									</label>
									<div className='relative'>
										<input
											type='text'
											value={providerAddressForm.city}
											onChange={(e) =>
												handleProviderCityInput(e.target.value)
											}
											onBlur={() => {
												window.setTimeout(
													() => setCitySuggestions([]),
													150,
												);
											}}
											onKeyDown={(event) => {
												if (event.key === "Escape")
													setCitySuggestions([]);
											}}
											className='h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none focus:border-primary'
											placeholder='Ej: Caracas'
										/>
										{citySuggestions.length > 0 ? (
											<ul className='absolute left-0 right-0 top-full z-10 mt-1 max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-md'>
												{citySuggestions.map((suggestion) => (
													<li
														key={`city-${suggestion.value}-${suggestion.label}`}
														className='border-b border-slate-100 last:border-b-0'
													>
														<button
															type='button'
															className='w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50'
															onMouseDown={(event) => {
																event.preventDefault();
																selectCitySuggestion(
																	suggestion,
																);
															}}
														>
															{suggestion.label}
														</button>
													</li>
												))}
											</ul>
										) : null}
									</div>
									{citySuggestStatus === "loading" ? (
										<p className='mt-1 text-xs text-slate-500'>
											Buscando sugerencias...
										</p>
									) : null}
									{providerAddressErrors.city ? (
										<p className='mt-1 text-xs text-red-500'>
											{providerAddressErrors.city}
										</p>
									) : null}
								</div>

								<div className='flex items-center justify-end gap-2 pt-2'>
									<button
										type='button'
										onClick={closeProviderAddressModal}
										className='cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50'
									>
										Cancelar
									</button>
									<button
										type='button'
										onClick={() => void handleSaveProviderAddress()}
										disabled={isSavingAddress}
										className='cursor-pointer rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60'
									>
										{isSavingAddress ? "Guardando..." : "Guardar"}
									</button>
								</div>
							</div>
						</div>
					</div>
				) : null}
			</>
		);
	};

	const renderMiCuenta = () => {
		const accountSections: Array<{
			key: AccountSectionKey;
			label: string;
			Icon: typeof FileUser;
		}> = [
			{ key: "datos", label: "Datos Personales", Icon: FileUser },
			{ key: "direcciones", label: "Direcciones", Icon: MapPin },
			{ key: "pagos", label: "Métodos de Pago", Icon: CreditCard },
			{ key: "seguridad", label: "Seguridad", Icon: KeyRound },
		];

		const accountContent: Record<AccountSectionKey, () => ReactNode> = {
			datos: renderDatosPersonales,
			direcciones: renderDirecciones,
			pagos: renderMetodosPago,
			seguridad: renderSeguridad,
		};

		return (
			<div className='grid grid-cols-1 gap-6 lg:grid-cols-[260px_minmax(0,1fr)]'>
				<aside className='rounded-2xl border border-slate-200 bg-slate-50/80 p-3'>
					<p className='px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500'>
						Mi cuenta
					</p>
					<div className='space-y-1'>
						{accountSections.map(({ key, label, Icon }) => (
							<button
								key={key}
								type='button'
								onClick={() => void handleAccountSectionChange(key)}
								className={`cursor-pointer flex w-full items-center gap-2 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition ${
									activeAccountSection === key
										? "bg-white text-primary shadow-sm"
										: "text-slate-600 hover:bg-white/70 hover:text-slate-800"
								}`}
							>
								<Icon className='h-4 w-4 shrink-0' />
								<span>{label}</span>
							</button>
						))}
					</div>
				</aside>

				<div className='min-w-0'>{accountContent[activeAccountSection]()}</div>
			</div>
		);
	};

	/* ── TAB: Mis Pedidos ── */
	const renderMisPedidos = () => {
		const hasAnyOrders = pedidosOrders.length > 0;
		const hasFilteredResults = filteredPedidosOrders.length > 0;

		return (
			<div>
				<div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
					<div>
						<h2 className='text-2xl font-semibold text-gray-800'>
							Historial de Pedidos
						</h2>
						<p className='text-sm text-gray-500'>
							Servicios que has realizado
						</p>
					</div>
					<button
						type='button'
						onClick={() =>
							setPedidosView(pedidosView === "table" ? "cards" : "table")
						}
						className='cursor-pointer inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50'
					>
						{pedidosView === "table" ? (
							<>
								<Grid3X3 className='h-4 w-4' /> Bloques
							</>
						) : (
							<>
								<Table2 className='h-4 w-4' /> Tabla
							</>
						)}
					</button>
				</div>

				{/* Filter buttons */}
				<div className='mb-6 flex flex-wrap gap-2'>
					<button
						type='button'
						onClick={() => setShowPedidosFilters((prev) => !prev)}
						className={`cursor-pointer inline-flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-medium transition ${
							showPedidosFilters
								? "border-primary bg-primary/10 text-primary"
								: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
						}`}
					>
						<Filter className='h-4 w-4' /> Filtros
					</button>
					<button
						type='button'
						onClick={() =>
							setPedidosQuickFilter((prev) =>
								prev === "completado" ? "all" : "completado",
							)
						}
						className={`cursor-pointer rounded-xl border px-4 py-2 text-sm font-medium transition ${
							pedidosQuickFilter === "completado"
								? "border-primary bg-primary/10 text-primary"
								: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
						}`}
					>
						Completado
					</button>
					<button
						type='button'
						onClick={() =>
							setPedidosQuickFilter((prev) =>
								prev === "en-proceso" ? "all" : "en-proceso",
							)
						}
						className={`cursor-pointer rounded-xl border px-4 py-2 text-sm font-medium transition ${
							pedidosQuickFilter === "en-proceso"
								? "border-primary bg-primary/10 text-primary"
								: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
						}`}
					>
						En Proceso
					</button>
					<button
						type='button'
						onClick={() =>
							setPedidosQuickFilter((prev) =>
								prev === "por-cotizar" ? "all" : "por-cotizar",
							)
						}
						className={`cursor-pointer rounded-xl border px-4 py-2 text-sm font-medium transition ${
							pedidosQuickFilter === "por-cotizar"
								? "border-primary bg-primary/10 text-primary"
								: "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
						}`}
					>
						Por Cotizar
					</button>
					<button
						type='button'
						onClick={clearPedidosFilters}
						className='cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50'
					>
						Todos
					</button>
				</div>

				{showPedidosFilters ? (
					<div className='mb-6'>
						<FiltersPedidos
							filters={pedidosFilters}
							onChange={setPedidosFilters}
							onClear={clearPedidosFilters}
							statusOptions={pedidosStatusOptions}
							showLocationFilter={false}
						/>
					</div>
				) : null}

				{!hasAnyOrders ? (
					<div className='rounded-2xl border border-slate-100 bg-slate-50 p-8 text-center text-sm text-slate-500'>
						No tienes órdenes registradas aún.
					</div>
				) : !hasFilteredResults ? (
					<div className='rounded-2xl border border-slate-100 bg-slate-50 p-8 text-center text-sm text-slate-500'>
						No hay pedidos que coincidan con los filtros seleccionados.
					</div>
				) : pedidosView === "table" ? (
					/* ── Table view ── */
					<div className='overflow-x-auto'>
						<table className='w-full table-fixed text-left text-sm'>
							<thead>
								<tr className='border-b border-slate-100 text-sm text-slate-500'>
									<th className='w-24 px-4 py-3 font-medium'>ID</th>
									<th className='w-44 px-4 py-3 font-medium'>
										Cliente
									</th>
									<th className='w-36 px-4 py-3 font-medium'>Fecha</th>
									<th className='w-40 px-4 py-3 font-medium'>Estado</th>
									<th className='w-40 px-4 py-3 font-medium'>
										<button
											type='button'
											onClick={() =>
												setTotalsCurrency((prev) =>
													prev === "USD" ? "Bs" : "USD",
												)
											}
											className='cursor-pointer inline-flex items-center rounded-lg px-2 py-1 text-slate-600 transition hover:bg-slate-100 hover:text-slate-800'
											aria-label='Cambiar moneda del total'
										>
											Total ({totalsCurrency})
										</button>
									</th>
									<th className='w-44 px-4 py-3 font-medium'>
										Acciones
									</th>
								</tr>
							</thead>
							<tbody>
								{filteredPedidosOrders.map((order) => {
									const isInProgressOrder = isOrderInProgressStatus(
										order.estado,
									);
									const canShowCotizarAction =
										(order.estado === "Dialogando" &&
											!isInProgressOrder) ||
										(!isCompletedOrderForQuotation(order.estado) &&
											!isInProgressOrder &&
											!order.hasPendingQuotation);
									const isWaitingOrder = isOrderWaitingStatus(
										order.estado,
									);

									return (
										<tr
											key={order.id}
											className='border-b border-slate-50 transition hover:bg-slate-50/50'
										>
											<td className='px-4 py-3.5 font-medium text-slate-800'>
												{order.numeroPedido ?? order.id}
											</td>
											<td className='truncate px-4 py-3.5 text-slate-700'>
												{order.cliente}
											</td>
											<td className='whitespace-nowrap px-4 py-3.5 text-slate-700'>
												{order.fecha}
											</td>
											<td className='px-4 py-3.5'>
												<span
													className={`inline-flex whitespace-nowrap rounded-full border px-3 py-0.5 text-xs font-medium ${statusStyles[order.estado] || "bg-slate-50 text-slate-700 border-slate-200"}`}
												>
													{order.estado}
												</span>
											</td>
											<td className='whitespace-nowrap px-4 py-3.5 font-semibold tabular-nums text-primary'>
												{formatOrderTotal(order)}
											</td>
											<td className='px-4 py-3.5'>
												{isInProgressOrder ? (
													<div className='flex items-center gap-2'>
														<button
															type='button'
															onClick={() =>
																void onCompleteOrder?.(
																	order,
																)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white transition hover:bg-emerald-500'
															aria-label='Completar pedido'
															title='Completar pedido'
														>
															<CheckCircle2 className='h-4 w-4' />
														</button>
														<button
															type='button'
															onClick={() =>
																void onReportOrder?.(
																	order,
																)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full border border-red-300 bg-white text-red-600 transition hover:bg-red-50'
															aria-label='Reportar pedido'
															title='Reportar pedido'
														>
															<AlertTriangle className='h-4 w-4' />
														</button>
													</div>
												) : isWaitingOrder ? (
													<div className='flex items-center gap-2'>
														<button
															type='button'
															onClick={() =>
																handleViewOrderDetails(
																	order,
																)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full border border-primary/30 text-primary transition hover:bg-primary/10'
															aria-label='Ver detalles'
														>
															<Eye className='h-4 w-4' />
														</button>
														<button
															type='button'
															onClick={() =>
																onCotizar?.(order)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white transition hover:bg-primary-hover'
															aria-label='Cotizar'
														>
															<ClipboardList className='h-4 w-4' />
														</button>
														<button
															type='button'
															onClick={() =>
																handleOpenChat(order)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-primary transition hover:bg-primary/20'
															aria-label='Abrir chat'
														>
															<MessageCircle className='h-4 w-4' />
														</button>
														<button
															type='button'
															onClick={() =>
																void onStartService?.(
																	order,
																)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white transition hover:bg-primary-hover'
															aria-label='Iniciar servicio'
															title='Iniciar servicio'
														>
															<Play className='h-4 w-4' />
														</button>
													</div>
												) : order.estado === "Dialogando" ? (
													<div className='flex items-center gap-2'>
														<button
															type='button'
															onClick={() =>
																handleViewOrderDetails(
																	order,
																)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full border border-primary/30 text-primary transition hover:bg-primary/10'
															aria-label='Ver detalles'
														>
															<Eye className='h-4 w-4' />
														</button>
														{canShowCotizarAction ? (
															<button
																type='button'
																onClick={() =>
																	onCotizar?.(order)
																}
																className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white transition hover:bg-primary-hover'
																aria-label='Cotizar'
															>
																<ClipboardList className='h-4 w-4' />
															</button>
														) : null}
														<button
															type='button'
															onClick={() =>
																handleOpenChat(order)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-primary transition hover:bg-primary/20'
															aria-label='Abrir chat'
														>
															<MessageCircle className='h-4 w-4' />
														</button>
													</div>
												) : (
													<div className='flex items-center gap-2'>
														<button
															type='button'
															onClick={() =>
																handleViewOrderDetails(
																	order,
																)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full border border-primary/30 text-primary transition hover:bg-primary/10'
															aria-label='Ver detalles'
														>
															<Eye className='h-4 w-4' />
														</button>
														{canShowCotizarAction ? (
															<button
																type='button'
																onClick={() =>
																	onCotizar?.(order)
																}
																className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white transition hover:bg-primary-hover'
																aria-label='Cotizar'
															>
																<ClipboardList className='h-4 w-4' />
															</button>
														) : null}
														<button
															type='button'
															onClick={() =>
																handleOpenChat(order)
															}
															className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-primary transition hover:bg-primary/20'
															aria-label='Abrir chat'
														>
															<MessageCircle className='h-4 w-4' />
														</button>
													</div>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				) : (
					/* ── Cards view (matching design) ── */
					<div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
						{filteredPedidosOrders.map((order) => {
							const isUrgent = /urgente|urgent/i.test(
								String(order.urgency ?? ""),
							);
							const isInProgressOrder = isOrderInProgressStatus(
								order.estado,
							);
							const canShowCotizarAction =
								(order.estado === "Dialogando" && !isInProgressOrder) ||
								(!isCompletedOrderForQuotation(order.estado) &&
									!isInProgressOrder &&
									!order.hasPendingQuotation);
							const isWaitingOrder = isOrderWaitingStatus(order.estado);
							const shouldShowCotizarAction =
								canShowCotizarAction || isWaitingOrder;
							const urgencyLabel = isUrgent ? "Urgente" : "No Urgente";
							const urgencyClass = isUrgent
								? "border-[#E93A1F] bg-[#FFD4CE] text-[#E93A1F]"
								: "border-green-200 bg-green-50 text-green-600";

							return (
								<div
									key={order.id}
									className='flex flex-col justify-between rounded-2xl border border-slate-100 bg-white p-5 shadow-sm'
								>
									{/* Header: X + urgency badge */}
									<div>
										<div className='mb-4 flex items-center justify-between'>
											<div className='flex gap-2'>
												<span
													className={`inline-flex rounded-full border px-3 py-0.5 text-xs font-semibold ${statusStyles[order.estado] || "bg-slate-50 text-slate-700 border-slate-200"}`}
												>
													{order.estado}
												</span>
												<span
													className={`inline-flex rounded-full border px-3 py-0.5 text-xs font-semibold ${urgencyClass}`}
												>
													{urgencyLabel}
												</span>
											</div>
										</div>

										{/* Info rows */}
										<div className='space-y-2 text-sm'>
											<div className='flex items-center justify-between'>
												<span className='text-slate-500'>
													Número de Pedido
												</span>
												<span className='text-lg font-bold text-slate-800'>
													{order.numeroPedido ?? order.id}
												</span>
											</div>
											<div className='flex items-center justify-between'>
												<span className='text-slate-500'>
													Fecha
												</span>
												<span className='text-slate-700'>
													{order.fecha}
												</span>
											</div>
											<div className='flex items-center justify-between'>
												<span className='text-slate-500'>
													Cliente
												</span>
												<span className='text-slate-700'>
													{order.cliente}
												</span>
											</div>
											<div className='flex items-center justify-between'>
												<span className='text-slate-500'>
													Total
												</span>
												<span className='font-semibold text-primary'>
													{formatOrderTotal(order)}
												</span>
											</div>
										</div>

										{/* Ubicación */}
										{order.ubicacion ? (
											<div className='mt-4 border-t border-slate-100 pt-3'>
												<p className='text-xs font-medium text-slate-500'>
													Ubicación
												</p>
												<p className='mt-0.5 text-sm text-slate-700'>
													{order.ubicacion}
												</p>
											</div>
										) : null}
									</div>

									{/* Footer: Action + Right Icons */}
									<div className='mt-5 flex items-center justify-between border-t border-slate-100 pt-4'>
										{isInProgressOrder ? (
											<div className='grid w-full grid-cols-2 gap-2'>
												<button
													type='button'
													onClick={() =>
														void onCompleteOrder?.(order)
													}
													className='cursor-pointer inline-flex items-center justify-center gap-1 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500'
												>
													<CheckCircle2 className='h-4 w-4' />
													Completar
												</button>
												<button
													type='button'
													onClick={() =>
														void onReportOrder?.(order)
													}
													className='cursor-pointer inline-flex items-center justify-center gap-1 rounded-full border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-50'
												>
													<AlertTriangle className='h-4 w-4' />
													Reportar
												</button>
											</div>
										) : (
											<>
												{order.estado === "Dialogando" ? (
													<button
														type='button'
														onClick={() => onCotizar?.(order)}
														className='cursor-pointer rounded-full border border-primary bg-white px-6 py-2 text-sm font-semibold text-primary transition hover:bg-primary/5'
													>
														Cotizar
													</button>
												) : isWaitingOrder ? (
													<button
														type='button'
														onClick={() =>
															void onStartService?.(order)
														}
														className='cursor-pointer rounded-full bg-primary px-6 py-2 text-sm font-semibold text-white transition hover:bg-primary-hover'
													>
														Iniciar servicio
													</button>
												) : (
													<button
														type='button'
														onClick={() =>
															handleViewOrderDetails(order)
														}
														className='cursor-pointer rounded-full border border-primary bg-white px-6 py-2 text-sm font-semibold text-primary transition hover:bg-primary/5'
													>
														Ver Detalles
													</button>
												)}
												<div className='flex items-center gap-2'>
													{order.estado === "Dialogando" ||
													isWaitingOrder ? (
														<button
															type='button'
															onClick={() =>
																handleViewOrderDetails(
																	order,
																)
															}
															className='cursor-pointer flex h-10 w-10 items-center justify-center rounded-full border border-primary/30 text-primary transition hover:bg-primary/10'
															aria-label='Ver detalles'
														>
															<Eye className='h-5 w-5' />
														</button>
													) : null}
													{shouldShowCotizarAction ? (
														<button
															type='button'
															onClick={() =>
																onCotizar?.(order)
															}
															className='cursor-pointer flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white transition hover:bg-primary-hover'
															aria-label='Cotizar'
														>
															<ClipboardList className='h-5 w-5' />
														</button>
													) : null}
													<button
														type='button'
														onClick={() =>
															handleOpenChat(order)
														}
														className='cursor-pointer flex h-10 w-10 items-center justify-center rounded-full bg-secondary text-primary transition hover:bg-primary/20'
														aria-label='Chat'
													>
														<MessageCircle className='h-5 w-5' />
													</button>
												</div>
											</>
										)}
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	};

	/* ── TAB: Mis Servicios ── */
	const renderMisServicios = () => (
		<div>
			<div className='mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
				<div>
					<h2 className='text-2xl font-semibold text-gray-800'>
						Mis Servicios
					</h2>
				</div>
				<button
					type='button'
					onClick={onAddService}
					disabled={disableAddService}
					className='cursor-pointer inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:opacity-60'
				>
					<Plus className='h-4 w-4' />
					Agregar Servicio
				</button>
			</div>

			{services.length === 0 ? (
				<div className='rounded-2xl border border-slate-100 bg-slate-50 p-8 text-center text-sm text-slate-500'>
					No tienes servicios registrados aún.
				</div>
			) : (
				<>
					<div className='mb-3 md:hidden'>
						<button
							type='button'
							onClick={() =>
								setTotalsCurrency((prev) =>
									prev === "USD" ? "Bs" : "USD",
								)
							}
							className='cursor-pointer inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-800'
							aria-label='Cambiar moneda del precio base'
						>
							Precio base ({totalsCurrency})
						</button>
					</div>

					<div className='grid gap-3 md:hidden'>
						{services.map((service) => (
							<article
								key={service.id}
								className='overflow-hidden rounded-2xl border border-slate-200 bg-white p-4'
							>
								<p className='line-clamp-2 text-sm font-semibold leading-snug text-slate-700'>
									{service.subcategoria}
								</p>
								<p className='mt-1 text-base font-bold tabular-nums text-primary'>
									{formatServiceBasePrice(service)}
								</p>
								<div className='mt-3 space-y-3 border-t border-slate-100 pt-3'>
									<div className='flex items-center justify-between gap-3'>
										<AvailabilitySwitch
											checked={service.activo}
											onToggle={() =>
												onToggleServiceStatus?.(service)
											}
											disabled={togglingServiceId === service.id}
											ariaLabel={`Estado de ${service.subcategoria}`}
											labelOn='Activo'
											labelOff='Inactivo'
										/>
									</div>
									<div className='flex items-center justify-end gap-2'>
										<button
											type='button'
											onClick={() => onViewService?.(service)}
											className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full border border-primary/30 text-primary transition hover:bg-primary/10'
											aria-label='Ver servicio'
										>
											<Eye className='h-4 w-4' />
										</button>
										<button
											type='button'
											onClick={() => onEditService?.(service)}
											className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-primary transition hover:bg-primary/20'
											aria-label='Editar servicio'
										>
											<Pencil className='h-4 w-4' />
										</button>
									</div>
								</div>
							</article>
						))}
					</div>

					<div className='hidden overflow-x-auto md:block'>
						<table className='w-full table-fixed text-left text-sm'>
							<thead>
								<tr className='border-b border-slate-100 text-sm text-slate-500'>
									<th className='w-2/5 px-4 py-3 font-medium'>
										Subcategoría
									</th>
									<th className='w-1/5 px-4 py-3 font-medium'>
										<button
											type='button'
											onClick={() =>
												setTotalsCurrency((prev) =>
													prev === "USD" ? "Bs" : "USD",
												)
											}
											className='cursor-pointer inline-flex items-center rounded-lg px-2 py-1 text-slate-600 transition hover:bg-slate-100 hover:text-slate-800'
											aria-label='Cambiar moneda del precio base'
										>
											Precio base ({totalsCurrency})
										</button>
									</th>
									<th className='w-1/5 px-4 py-3 font-medium'>
										Estado
									</th>
									<th className='w-1/5 px-4 py-3 font-medium'>
										Acciones
									</th>
								</tr>
							</thead>
							<tbody>
								{services.map((service) => (
									<tr
										key={service.id}
										className='border-b border-slate-50 transition hover:bg-slate-50/50'
									>
										<td className='truncate px-4 py-3.5 text-slate-700'>
											{service.subcategoria}
										</td>
										<td className='whitespace-nowrap px-4 py-3.5 font-semibold tabular-nums text-primary'>
											{formatServiceBasePrice(service)}
										</td>
										<td className='px-4 py-3.5'>
											<AvailabilitySwitch
												checked={service.activo}
												onToggle={() =>
													onToggleServiceStatus?.(service)
												}
												disabled={
													togglingServiceId === service.id
												}
												ariaLabel={`Estado de ${service.subcategoria}`}
												labelOn='Activo'
												labelOff='Inactivo'
											/>
										</td>
										<td className='px-4 py-3.5'>
											<div className='inline-flex items-center gap-2'>
												<button
													type='button'
													onClick={() =>
														onViewService?.(service)
													}
													className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full border border-primary/30 text-primary transition hover:bg-primary/10'
													aria-label='Ver servicio'
												>
													<Eye className='h-4 w-4' />
												</button>
												<button
													type='button'
													onClick={() =>
														onEditService?.(service)
													}
													className='cursor-pointer flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-primary transition hover:bg-primary/20'
													aria-label='Editar servicio'
												>
													<Pencil className='h-4 w-4' />
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</>
			)}
		</div>
	);

	/* ── TAB: Preguntas Frecuentes ── */
	const renderPreguntasFrecuentes = () => (
		<>
			<div className='space-y-6'>
				<div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
					<div>
						<h2 className='text-2xl font-semibold text-gray-800'>
							Preguntas Frecuentes
						</h2>
					</div>
					<button
						type='button'
						onClick={openCreateFaqModal}
						disabled={
							!onCreateFaq ||
							resolvedFaqCategories.length === 0 ||
							isSavingFaq
						}
						className='cursor-pointer inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
					>
						<Plus className='h-4 w-4' />
						Agregar pregunta
					</button>
				</div>

				<div className='flex flex-wrap gap-2'>
					<button
						type='button'
						onClick={() => setFaqCategoryFilter("all")}
						className={
							"cursor-pointer rounded-full border px-5 py-2 text-sm font-medium transition " +
							(faqCategoryFilter === "all"
								? "border-primary bg-primary text-white"
								: "border-slate-300 bg-white text-slate-500 hover:border-slate-400 hover:text-slate-700")
						}
					>
						Todas
					</button>
					{resolvedFaqCategories.map((category) => (
						<button
							key={category.id}
							type='button'
							onClick={() => setFaqCategoryFilter(category.id)}
							className={
								"cursor-pointer rounded-full border px-5 py-2 text-sm font-medium transition " +
								(faqCategoryFilter === category.id
									? "border-primary bg-primary text-white"
									: "border-slate-300 bg-white text-slate-500 hover:border-slate-400 hover:text-slate-700")
							}
						>
							{category.label}
						</button>
					))}
				</div>

				{groupedFaqs.length === 0 ? (
					<div className='rounded-2xl border border-slate-100 bg-slate-50 p-8 text-center text-sm text-slate-500'>
						No tienes preguntas frecuentes registradas para este filtro.
					</div>
				) : (
					<div className='space-y-8'>
						{groupedFaqs.map((group) => (
							<section key={group.id} className='space-y-3'>
								<h3 className='text-lg font-semibold text-slate-800 sm:text-xl'>
									{group.label}
								</h3>
								<div className='space-y-3'>
									{group.items.map((faq) => {
										const isOpen = expandedFaqId === faq.id;

										return (
											<div
												key={faq.id}
												className='overflow-hidden rounded-2xl border border-slate-300 bg-white'
											>
												<button
													type='button'
													onClick={() =>
														setExpandedFaqId((prev) =>
															prev === faq.id
																? null
																: faq.id,
														)
													}
													className='cursor-pointer flex w-full items-center justify-between px-4 py-4 text-left'
												>
													<span className='text-base font-semibold text-slate-800 sm:text-base'>
														{faq.question}
													</span>
													{isOpen ? (
														<ChevronUp className='h-5 w-5 text-primary' />
													) : (
														<ChevronDown className='h-5 w-5 text-slate-400' />
													)}
												</button>

												{isOpen ? (
													<div className='border-t border-slate-300 px-4 py-3'>
														<p className='text-sm leading-relaxed text-slate-600 sm:text-base'>
															{faq.answer}
														</p>
														<div className='mt-4 flex items-center gap-2'>
															<button
																type='button'
																onClick={() =>
																	openEditFaqModal(faq)
																}
																disabled={
																	!onUpdateFaq ||
																	isSavingFaq
																}
																className='cursor-pointer rounded-lg p-1 text-primary transition hover:bg-secondary/40 disabled:cursor-not-allowed disabled:opacity-60'
																aria-label='Editar pregunta'
															>
																<Pencil className='h-5 w-5' />
															</button>
															<button
																type='button'
																onClick={() =>
																	void handleDeleteFaqItem(
																		faq,
																	)
																}
																disabled={
																	!onDeleteFaq ||
																	isSavingFaq
																}
																className='cursor-pointer rounded-lg p-1 text-error-text transition hover:bg-error-text/10 disabled:cursor-not-allowed disabled:opacity-60'
																aria-label='Eliminar pregunta'
															>
																<Trash2 className='h-5 w-5' />
															</button>
														</div>
													</div>
												) : null}
											</div>
										);
									})}
								</div>
							</section>
						))}
					</div>
				)}
			</div>

			{isFaqModalOpen ? (
				<div className='fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-black/50 px-4 py-6'>
					<div className='relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white'>
						<button
							type='button'
							onClick={closeFaqModal}
							className='cursor-pointer absolute right-4 top-4 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600'
							aria-label='Cerrar modal'
						>
							<X className='h-5 w-5' />
						</button>

						<div className='flex-1 space-y-5 overflow-y-auto overscroll-contain px-6 pb-6 pt-14'>
							<label className='block space-y-2'>
								<span className='text-sm font-semibold text-slate-700'>
									Categoría *
								</span>
								<select
									value={faqForm.categoryId}
									onChange={(event) =>
										handleFaqFieldChange(
											"categoryId",
											event.target.value,
										)
									}
									className='h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-primary'
								>
									<option value=''>Selecciona una categoría</option>
									{resolvedFaqCategories.map((category) => (
										<option
											key={category.id}
											value={String(category.id)}
										>
											{category.label}
										</option>
									))}
								</select>
								{faqFormErrors.categoryId ? (
									<p className='text-sm font-semibold text-rose-600'>
										{faqFormErrors.categoryId}
									</p>
								) : null}
							</label>

							<label className='block space-y-2'>
								<span className='text-sm font-semibold text-slate-700'>
									Pregunta *
								</span>
								<input
									type='text'
									value={faqForm.question}
									onChange={(event) =>
										handleFaqFieldChange(
											"question",
											event.target.value,
										)
									}
									placeholder='Escribe la pregunta'
									className='h-11 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-700 outline-none transition focus:border-primary'
								/>
								{faqFormErrors.question ? (
									<p className='text-sm font-semibold text-rose-600'>
										{faqFormErrors.question}
									</p>
								) : null}
							</label>

							<label className='block space-y-2'>
								<span className='text-sm font-semibold text-slate-700'>
									Respuesta *
								</span>
								<textarea
									value={faqForm.answer}
									onChange={(event) =>
										handleFaqFieldChange("answer", event.target.value)
									}
									rows={6}
									placeholder='Escribe la respuesta detallada aquí...'
									className='w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-primary'
								/>
								{faqFormErrors.answer ? (
									<p className='text-sm font-semibold text-rose-600'>
										{faqFormErrors.answer}
									</p>
								) : null}
							</label>

							<div className='flex items-center justify-between gap-3 pt-2'>
								<button
									type='button'
									onClick={closeFaqModal}
									disabled={isSavingFaq}
									className='cursor-pointer flex-1 rounded-xl border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60'
								>
									Cancelar
								</button>
								<button
									type='button'
									onClick={() => void handleSubmitFaq()}
									disabled={isSavingFaq}
									className='cursor-pointer flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60'
								>
									<Save className='h-5 w-5' />
									{isSavingFaq ? "Guardando..." : "Guardar"}
								</button>
							</div>
						</div>
					</div>
				</div>
			) : null}
		</>
	);

	/* ── TAB: Estadísticas ── */
	const renderEstadisticas = () => {
		const resolvedStats = stats ?? {
			totalPedidos: 0,
			completados: 0,
			ingreso: 0,
			porCobrar: 0,
			pendientesCobro: 0,
			rating: 0,
		};
		return (
			<div className='space-y-8'>
				{/* Stats cards */}
				<div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
					<div className='rounded-2xl border border-slate-100 bg-white p-5 shadow-sm'>
						<div className='mb-2 flex items-center justify-between'>
							<span className='text-sm text-slate-500'>
								Servicios Completados
							</span>
							<CheckCircle2 className='h-5 w-5 text-primary' />
						</div>
						<p className='text-3xl font-bold text-primary'>
							{resolvedStats.completados}
						</p>
						<p className='mt-1 text-xs text-slate-400'>
							de {resolvedStats.totalPedidos} pedidos
						</p>
					</div>
					<div className='rounded-2xl border border-slate-100 bg-white p-5 shadow-sm'>
						<div className='mb-2 flex items-center justify-between'>
							<span className='text-sm text-slate-500'>
								Ingresos Este Mes
							</span>
							<Eye className='h-5 w-5 text-[#008235]' />
						</div>
						<p className='text-3xl font-bold text-[#008235]'>
							${resolvedStats.ingreso.toLocaleString("es-VE")}
						</p>
						{/*<p className='mt-1 text-xs text-slate-400'>↗ vs mes anterior</p>*/}
					</div>
					<div className='rounded-2xl border border-slate-100 bg-white p-5 shadow-sm'>
						<div className='mb-2 flex items-center justify-between'>
							<span className='text-sm text-slate-500'>Calificación</span>
							<Star className='h-5 w-5 text-[#894C00]' />
						</div>
						<p className='text-3xl font-bold text-slate-800'>
							{resolvedStats.rating.toFixed(1)}{" "}
							<span className='text-lg font-normal text-slate-400'>
								/ 5
							</span>
						</p>
						<div className='mt-1 flex gap-0.5'>
							{[1, 2, 3, 4, 5].map((star) => (
								<Star
									key={star}
									className={`h-4 w-4 ${
										star <= Math.round(resolvedStats.rating)
											? "fill-[#894C00] text-[#894C00]"
											: "text-[#894C00]"
									}`}
								/>
							))}
						</div>
					</div>
				</div>

				{/* Reseñas de Clientes
				<section>
					<h3 className='mb-1 text-xl font-semibold text-gray-800'>
						Reseñas de Clientes
					</h3>
					<p className='mb-4 text-sm text-gray-500'>
						Lo que tus clientes dicen sobre ti
					</p>
					<div className='space-y-4'>
						<div className='rounded-2xl border border-slate-100 bg-white p-5'>
							<div className='flex items-center justify-between'>
								<div className='flex items-center gap-3'>
									<div className='flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-600'>
										MG
									</div>
									<div>
										<p className='text-sm font-semibold text-slate-800'>
											María González
										</p>
										<p className='text-xs text-slate-400'>
											Reparación de closet
										</p>
									</div>
								</div>
								<span className='text-xs text-slate-400'>19/1/2025</span>
							</div>
							<div className='mt-2 flex gap-0.5'>
								{[1, 2, 3, 4, 5].map((s) => (
									<Star
										key={s}
										className='h-4 w-4 fill-[#894C00] text-[#894C00]'
									/>
								))}
							</div>
							<p className='mt-2 text-sm text-slate-600'>
								Excelente trabajo, muy profesional y puntual. Quedé muy
								satisfecha con la reparación de mi closet.
							</p>
						</div>
						<div className='rounded-2xl border border-slate-100 bg-white p-5'>
							<div className='flex items-center justify-between'>
								<div className='flex items-center gap-3'>
									<div className='flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-600'>
										LT
									</div>
									<div>
										<p className='text-sm font-semibold text-slate-800'>
											Luis Torres
										</p>
										<p className='text-xs text-slate-400'>
											Deck de madera
										</p>
									</div>
								</div>
								<span className='text-xs text-slate-400'>15/1/2025</span>
							</div>
							<div className='mt-2 flex gap-0.5'>
								{[1, 2, 3, 4, 5].map((s) => (
									<Star
										key={s}
										className='h-4 w-4 fill-[#894C00] text-[#894C00]'
									/>
								))}
							</div>
							<p className='mt-2 text-sm text-slate-600'>
								Increíble trabajo en el deck. Superó mis expectativas. Lo
								recomiendo 100%.
							</p>
						</div>
						<div className='rounded-2xl border border-slate-100 bg-white p-5'>
							<div className='flex items-center justify-between'>
								<div className='flex items-center gap-3'>
									<div className='flex h-10 w-10 items-center justify-center rounded-full bg-slate-200 text-sm font-semibold text-slate-600'>
										CR
									</div>
									<div>
										<p className='text-sm font-semibold text-slate-800'>
											Carmen Ruiz
										</p>
										<p className='text-xs text-slate-400'>Repisas</p>
									</div>
								</div>
								<span className='text-xs text-slate-400'>9/1/2025</span>
							</div>
							<div className='mt-2 flex gap-0.5'>
								{[1, 2, 3].map((s) => (
									<Star
										key={s}
										className='h-4 w-4 fill-[#894C00] text-[#894C00]'
									/>
								))}
								{[4, 5].map((s) => (
									<Star key={s} className='h-4 w-4 text-[#894C00]' />
								))}
							</div>
							<p className='mt-2 text-sm text-slate-600'>
								Buen servicio, llegó a tiempo y el trabajo quedó bien.
								Solo un pequeño detalle en el acabado.
							</p>
						</div>
					</div>
				</section> */}
			</div>
		);
	};

	/* ── TAB: Seguridad ── */
	const { session } = useAuthSessionContext();
	const userId = Number(session?.user?.id ?? 0);
	const { changePassword, loading: isChangingPassword } = useChangeUserPassword(userId);
	const [showCurrentPassword, setShowCurrentPassword] = useState(false);
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isDeletingAccount, setIsDeletingAccount] = useState(false);

	const clearPasswordFields = () => {
		setCurrentPassword("");
		setNewPassword("");
		setConfirmPassword("");
	};

	const handleUpdatePassword = async () => {
		if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) {
			showAlert({
				title: "Campos requeridos",
				message: "Completa todos los campos para actualizar la contraseña.",
				variant: "warning",
			});
			return;
		}

		if (newPassword !== confirmPassword) {
			showAlert({
				title: "Contraseñas no coinciden",
				message: "La nueva contraseña y su confirmación deben ser iguales.",
				variant: "warning",
			});
			return;
		}

		if (currentPassword.trim() === newPassword.trim()) {
			showAlert({
				title: "Contraseña igual",
				message: "La nueva contraseña no puede ser igual a la contraseña actual.",
				variant: "warning",
			});
			return;
		}

		if (!userId) {
			showAlert({
				title: "Usuario inválido",
				message:
					"No se pudo identificar al usuario para actualizar la contraseña.",
				variant: "error",
			});
			return;
		}

		const confirmed = await requestConfirmation({
			title: "Cambio de Contraseña",
			message: "¿Estás seguro de que quieres modificar tu contraseña?",
			confirmLabel: "Sí",
			cancelLabel: "No",
		});

		if (!confirmed) return;

		try {
			await changePassword(currentPassword, newPassword);
			clearPasswordFields();
			showAlert({
				title: "Contraseña actualizada",
				message: "Tu contraseña fue actualizada correctamente.",
				variant: "success",
			});
		} catch (error: unknown) {
			const message =
				error instanceof Error
					? error.message
					: "No se pudo actualizar la contraseña. Intenta nuevamente.";
			showAlert({
				title: "Error",
				message,
				variant: "error",
			});
		}
	};

	const handleDeleteAccount = async () => {
		if (!userId) {
			showAlert({
				title: "Usuario inválido",
				message: "No se pudo identificar tu cuenta para eliminarla.",
				variant: "error",
			});
			return;
		}

		const first = await requestConfirmation({
			title: "Eliminar cuenta",
			message:
				"¿Estás seguro de que deseas eliminar tu cuenta? Esta acción es permanente e irreversible.",
			confirmLabel: "Sí, eliminar",
			cancelLabel: "Cancelar",
			variant: "danger",
		});
		if (!first) return;

		const second = await requestConfirmation({
			title: "Confirmar eliminación",
			message:
				"Esta es tu última oportunidad. Una vez eliminada no podrás recuperar tu cuenta ni acceder a ella. ¿Confirmas la eliminación permanente?",
			confirmLabel: "Confirmar eliminación",
			cancelLabel: "Cancelar",
			variant: "danger",
		});
		if (!second) return;

		setIsDeletingAccount(true);
		try {
			await requestApi("POST", "/banned-user", {
				user_id: userId,
				reason: "Cuenta eliminada por solicitud del usuario",
			});
			await logout();
			window.location.replace("/login");
		} catch (error: unknown) {
			const message =
				error instanceof Error
					? error.message
					: "No se pudo eliminar la cuenta. Intenta nuevamente.";
			showAlert({ title: "Error", message, variant: "error" });
		} finally {
			setIsDeletingAccount(false);
		}
	};

	const renderSeguridad = () => (
		<div className='space-y-8'>
			{/* Header */}
			<div>
				<h2 className='text-2xl font-semibold text-gray-800'>Seguridad</h2>
				<p className='text-sm text-gray-500'>
					Gestiona tu contraseña y configuración de seguridad
				</p>
			</div>

			{/* Cambiar Contraseña section */}
			<div className='space-y-6'>
				{/* Section icon + title */}
				<div className='flex items-center gap-3'>
					<div className='flex h-12 w-12 items-center justify-center rounded-full bg-primary/10'>
						<KeyRound className='h-6 w-6 text-primary' />
					</div>
					<div>
						<h3 className='text-base font-semibold text-gray-800'>
							Cambiar Contraseña
						</h3>
						<p className='text-sm text-gray-400'>
							Actualiza tu contraseña periódicamente
						</p>
					</div>
				</div>

				{/* Password fields */}
				<div className='space-y-5'>
					<PasswordField
						label='Contraseña Actual'
						placeholder='Ingresa tu contraseña actual'
						show={showCurrentPassword}
						onToggle={() => setShowCurrentPassword((v) => !v)}
						value={currentPassword}
						onChange={setCurrentPassword}
					/>
					<PasswordField
						label='Nueva Contraseña'
						placeholder='Ingresa tu nueva contraseña'
						show={showNewPassword}
						onToggle={() => setShowNewPassword((v) => !v)}
						value={newPassword}
						onChange={setNewPassword}
					/>
					<PasswordField
						label='Confirmar Nueva Contraseña'
						placeholder='Confirma tu nueva contraseña'
						show={showConfirmPassword}
						onToggle={() => setShowConfirmPassword((v) => !v)}
						value={confirmPassword}
						onChange={setConfirmPassword}
					/>
				</div>

				{/* Full-width button */}
				<button
					type='button'
					onClick={() => void handleUpdatePassword()}
					disabled={isChangingPassword}
					className='cursor-pointer w-full rounded-xl bg-primary py-3 text-sm font-semibold text-white transition hover:bg-primary-hover'
				>
					{isChangingPassword ? "Actualizando..." : "Actualizar Contraseña"}
				</button>
			</div>

			{/* Eliminar cuenta */}
			<div className='rounded-2xl border border-rose-200 bg-rose-50 p-5 sm:p-6'>
				<div className='mb-4 flex items-center gap-3'>
					<div className='flex h-12 w-12 items-center justify-center rounded-full bg-rose-100'>
						<Trash2 className='h-6 w-6 text-rose-600' />
					</div>
					<div>
						<h3 className='text-base font-semibold text-rose-700'>
							Eliminar Cuenta
						</h3>
						<p className='text-sm text-rose-400'>
							Esta acción es permanente e irreversible
						</p>
					</div>
				</div>
				<p className='mb-4 text-sm text-rose-600'>
					Al eliminar tu cuenta perderás acceso a todos tus datos, servicios,
					historial de pedidos y configuraciones. No podrás recuperarla.
				</p>
				<button
					type='button'
					onClick={() => void handleDeleteAccount()}
					disabled={isDeletingAccount}
					className='cursor-pointer w-full rounded-xl bg-rose-600 py-3 text-sm font-semibold text-white transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60'
				>
					{isDeletingAccount ? "Eliminando cuenta..." : "Eliminar mi cuenta"}
				</button>
			</div>
		</div>
	);

	/* ── Tab content map ── */
	const tabContent: Record<TabKey, () => ReactNode> = {
		solicitudes: renderMisSolicitudes,
		pedidos: renderMisPedidos,
		servicios: renderMisServicios,
		faq: renderPreguntasFrecuentes,
		cuenta: renderMiCuenta,
		estadisticas: renderEstadisticas,
	};

	/* ============================================================
	   RENDER
	   ============================================================ */
	return (
		<>
			<div className='mx-auto w-full p-4 md:p-8'>
				{/* ── Tab navigation bar ── */}
				<nav className='mb-6 border-b border-slate-200'>
					<div className='flex gap-6 overflow-x-auto'>
						{TABS.map((tab) => (
							<button
								key={tab.key}
								type='button'
								onClick={() => void handleTabChange(tab.key)}
								className={
									"cursor-pointer whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition " +
									(activeTab === tab.key
										? "border-primary text-primary"
										: "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700")
								}
							>
								{tab.label}
							</button>
						))}
					</div>
				</nav>

				{/* ── Tab content ── */}
				<div className='overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-xl shadow-slate-200/50'>
					<div className='p-6 md:p-8'>{tabContent[activeTab]()}</div>
				</div>
			</div>
			<AlertModal
				open={alertState.open}
				title={alertState.title}
				message={alertState.message}
				variant={alertState.variant}
				confirmText={alertState.confirmText}
				onClose={handleCloseAlert}
			/>
			<ConfirmModal
				open={confirmState.open}
				title={confirmState.title}
				message={confirmState.message}
				variant={confirmState.variant}
				confirmLabel={confirmState.confirmLabel}
				cancelLabel={confirmState.cancelLabel}
				onConfirm={handleConfirmAccept}
				onCancel={handleConfirmCancel}
			/>
		</>
	);
};
